// Catalog.ID Client — Organization Registration Wizard
// ============================================================================
// 11-step wizard (registration-restructure §6, M5; §6 round-6 amendment, M13):
//   Terms → Domain → Keygen (org + delegate, throwaway credentials, silent) →
//   Payment → Completion → Domain Verify → Device Setup →
//   Email verify → Phone verify → Org Recovery Kit opt-in → Finalize & Print
//
// The email and phone steps belong to the FOUNDING DELEGATE (round 6, §13.3):
// every delegate now verifies both, which is what lets the Delegate Membership
// Kit carry an Account Unlock Sheet. They run after Completion, so the session
// and the keys in memory are already the delegate's, and they are the shared
// implementation from auth/claim-verification-steps.ts configured with the
// delegate's own attr_encrypt pair.
//
// Finalize-at-print, org edition: registration runs on THROWAWAY credentials;
// the entropy ceremony mints the final DELEGATE password + supersecret + XRPL
// master key AND the final ORG supersecret in one run (round 6, §13.1 — the
// founding delegate gets an XRPL key, the organization does not). Both halves
// run through the shared finalize sequence (auth/finalize-credentials.ts): the
// delegate bag re-encrypts via the account replace-keybag primitive, the ORG
// bag via the org-scoped endpoint, whose challenge is signed with the org's
// main_signing_key (held in memory by this wizard). The ORG PASSWORD is a
// never-disclosed throwaway that lives only for the registration call — never in
// `shared`, so never in the onboarding stash — and finalize RETIRES it with the
// same organization-signed step-up (docs/cid-governance.md M15; login refuses
// organization accounts anyway). The printed org page carries the supersecret
// only, with an explanation that day-to-day access is always through delegate
// admin accounts.
//
// The printed documents (round 5, 2026-09-03): TWO separate kits, each its own
// print job — the Organization Membership Kit (cover → introduction → org
// credentials → org recovery sheets, with the Organization Identity Agreement
// appended) and the founding delegate's Delegate Membership Kit (cover →
// introduction → delegate credentials → Account Unlock Sheet → XRPL Master
// Key, with the Delegate Terms appended). The
// type-back gate covers BOTH: one line of the org supersecret and one of the
// delegate supersecret, read off the respective papers. The ORGANIZATION kit
// still has no Account Unlock Sheet — the organization account has no
// user-facing password and no channels of its own — but the founding
// delegate's kit now does (round 6, §13.3), sealed against the email and phone
// claims the delegate verified two steps earlier and issued just before
// finalize (never re-issued on a reprint, §11).

import { Wizard, createElement, createProgressBar, createCopyButton } from '@catalog-shell';
import type { WizardAPI, WizardStep, WizardStepContext } from '@catalog-shell';
import type { Content } from 'pdfmake/interfaces';
import { renderDeviceSetup, encryptLabelForDevice } from '../components/device-setup';
import { createEntropyCeremony, type EntropyCeremonyResult } from '../components/entropy-ceremony';
import { buildFrontPage } from '../components/booklet/front-page';
import { buildCredentialsPage } from '../components/booklet/credentials-page';
import { buildDelegateKit } from '../components/booklet/delegate-kit';
import {
    buildBitcashWalletPage, BITCASH_WALLET_PAGE_TITLE,
} from '../components/booklet/bitcash-wallet-page';
import { renderLoginCodeStep } from '../components/login-code-step';
import { base32Encode, base32Decode } from '../crypto/totp';
import { buildRecoveryCardPages, buildRecoveryCardCopySheets } from '../components/booklet/recovery-card-page';
import { createRecoveryKitCardFields } from '../components/recovery-kit-card-fields';
import { REGISTRATION_MIN_EXTRA_COPIES } from '../components/recovery-kit-presets';
import { buildCoverPage } from '../components/booklet/cover-page';
import { printBooklet, type BookletPrintOptions, type CopySheet } from '../components/booklet/booklet';
import { createPrintButton, PHYSICAL_PRINTER_NOTE } from '../components/print-button';
import { buildKitLayout, recoverySheetSections, recoveryCopyTitles } from '../components/booklet/kit-layout';
import { bookletTitle, formatVersion, type AccountHeaderData } from '../components/booklet/shared';
import { DELEGATE_TERMS_ASSET, ORGANIZATION_TERMS_ASSET } from '../components/terms-asset';
import { createAgreementLink } from '../components/agreement-link';
import { documentSetPageCount, printerCheckboxLabel } from '../components/booklet/page-estimate';
import { generatePassword, computeAuthHash } from '../crypto/credentials';
import { generateSupersecret } from '../crypto/supersecret';
import { kemKeygen } from '../crypto/kem';
import { signingKeygen } from '../crypto/signing';
import { encryptKeyBag, isBagOnlyKeyId, getKeyBagEntry } from '../crypto/keybag';
import { XRPL_MASTER_KEY_ID } from '../crypto/xrpl';
import {
    finalizeCredentials, orgFinalizeDeps, rememberDeviceKey, deviceCopyTarget,
} from './finalize-credentials';
import type { KeyBag } from '../crypto/keybag';
import { encryptClaim, wrapPrivateKey, reEncryptClaimKey } from '../crypto/claims';
import {
    emailVerificationStep, phoneVerificationStep, type ClaimStepsConfig,
} from './claim-verification-steps';
import { buildRecoveryKit, type RecoveryKit } from '../crypto/recovery-kit';
import { decodeRecoveryKitEntry } from '../crypto/recovery-kit-entry';
import * as aes from '../crypto/aes';
import * as delegateApi from '../api/delegate';
import { adoptFinalizedOrgBag, walletKeyFromBag } from './org-wallet-custody';
import * as organizationApi from '../api/organization';
import * as accountApi from '../api/account';
import { isTotpRequired } from '../api/types';
import * as claimsApi from '../api/claims';
import * as deviceApi from '../api/device';
import { api } from '../api/client';
import { session } from '../state/session';
import { keys } from '../state/keys';
import { identity } from '../state/identity';
import * as deviceStorage from '../state/device-storage';
import {
    freshNonce, getCancelUrl, getSuccessUrl, createPayment, pollUntilComplete,
    persistRegistrationState, clearRegistrationState,
    isTestHookActive, mintLocalTestProof, startPopupCheckoutAndAwaitProof,
    fetchRegistrationConfig, type DefaultTokenTerms,
} from './payment-flow';
import { mintedDefaultTokenEntry } from '../key-grant/bitcash-token-cache';
import { paymentStepOffer, waivedContinueUsesCode } from './registration-gate';
import { inspectVoucher, purchaseVouchers } from '../api/voucher';
import { convertProofOnce, needsProofConversion } from './proof-conversion';
import {
    normalizeVoucherCode, deriveVoucherSecrets, voucherCodeHash, type VoucherSecrets, formatVoucherCode,
    VOUCHER_CODE_PLACEHOLDER,
} from './voucher-code';
import { renderCheckedVoucher } from './checked-voucher-step';
import { mintVoucherBatch, voucherSpecOf, type MintedVoucher } from './voucher-mint';
import { createBitsField } from '../components/bits-field';
import { setUpWallet, walletSetupNotice, type WalletSetupResult } from './bits-release';
import { planWalletSetup } from './wallet-setup-plan';
import { publishOutcomeNotice, publishReceiveAddress } from './receive-address-claim';
import { bitcashWorker } from '../state/bitcash-context';
import {
    lockKeyFor, displayWalletKey, generateWalletKeyFrom, TYPE_CASH,
} from '../crypto/bitcash';
import { registrationWalletKey } from '../state/bitcash-wallets-store';
import {
    setDeviceSlot, rememberSlotKey, DEFAULT_TOKEN_SLOT, orgDefaultTokenSlot,
} from '../state/device-slots';
import { deriveInvitationSecrets, formatInvitationCode, normaliseInvitationCode } from './invitation-document';
import { classifyEntryCode } from './register-entry-view';
import { resolveOrganizationEntryCode, applyOrganizationEntryCode } from './org-entry-code';
import { inspectInvitation } from '../api/invitation';
import {
    loadOnboardingStash, saveOnboardingStash, readKitChoices, writeKitChoices,
    kitEntryFromChoices, kitExtraCopiesFor,
    readBitsStash,
} from './onboarding-stash';
import { completeOnboardingThenWipe } from './onboarding-completion';
import { finishLaterFrom, keepsRegistrationOnExit } from './wizard-exit';
import { isDashboardContext, dashboardHref } from '../state/dashboard-context';
import { getStoredOrigin, nodeHostFor } from '../state/origins';

export interface OrgRegistrationWizardCallbacks {
    onComplete: () => void;
    onCancel: () => void;
}

export interface OrgRegistrationWizardOptions {
    /** True when resuming — after the Stripe redirect, or from the login
     *  screen's unfinished-registration banner. */
    resumeFromPayment?: boolean;
    /** A registration code the entry page (§14.2) already read with the node —
     *  unredeemed, and for an organization registration. The Code step shows
     *  it, the Payment step names it and offers Continue, and the Completion
     *  step redeems it. */
    voucherCode?: string;
    /** An ORGANIZATION invitation code (`INV-…`, registration-restructure §14.3),
     *  already confirmed by `inspect` on the entry page. The fee is paid, so the
     *  Payment step becomes one line; the completion step redeems the invitation
     *  instead of a voucher. */
    invitationCode?: string;
    /** The domain that invitation fixes, if it fixes one. The Domain step is then
     *  prefilled and READ-ONLY: the buyer paid for this domain, and a founder who
     *  typed a different one would be refused at begin anyway. */
    invitationDomain?: string;
}

export function renderOrgRegistrationWizard(
    container: HTMLElement,
    callbacks: OrgRegistrationWizardCallbacks,
    options: OrgRegistrationWizardOptions = {},
): void {
    container.innerHTML = '';

    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card cid-auth-card--wide' });
    wrapper.appendChild(card);
    container.appendChild(wrapper);

    const shared: Record<string, unknown> = {};
    let restoredPersonalToken: string | undefined;
    let resumeFinalize = false;
    if (options.resumeFromPayment) {
        const stash = loadOnboardingStash();
        if (stash && stash.registrationType === 'organization') {
            Object.assign(shared, stash.shared);
            if (stash.registrationId) shared['registrationId'] = stash.registrationId;
            if (stash.nonce) shared['nonce'] = stash.nonce;
            // The voucher outlives the proof, so a resume days later still has a
            // usable authorization even though the proof token expired long ago.
            if (stash.voucherCode) shared['voucherCode'] = stash.voucherCode;
            // The CODE, not either derivative (§14.2) — both are one scrypt away.
            if (stash.invitationCode) shared['invitationCode'] = stash.invitationCode;
            restoredPersonalToken = stash.personalSessionToken;
            // A reprint must REUSE the sealed sheet — re-issuing bumps the epoch
            // and kills the one already on paper (§11).
            if (stash.resetCardSealedToken) shared['resetCardSealedToken'] = stash.resetCardSealedToken;
            if (stash.resetCardCreatedAt) shared['resetCardCreatedAt'] = stash.resetCardCreatedAt;
            resumeFinalize = stash.finalizeStarted === true;
        }
    }

    // If the personal session was lost during the Stripe redirect but we captured
    // the token at payment time, restore it on the API client so the resumed
    // /api/organization/register call carries the right Authorization.
    if (restoredPersonalToken && !api.getToken() && !shared['orgCid']) {
        api.setToken(restoredPersonalToken);
    }

    // A code the entry page already read with the node. Not a redemption: the
    // Code step shows it, the Payment step confirms it and the Completion step
    // spends it.
    if (options.voucherCode) shared['checkedVoucherCode'] = options.voucherCode;
    if (options.invitationCode) shared['invitationCode'] = options.invitationCode;
    if (options.invitationDomain) {
        shared['invitationDomain'] = options.invitationDomain;
        // Prefilled AND used: the domain step's field is read-only below, and the
        // domain claim is built from `shared['domain']` like any other.
        shared['domain'] = options.invitationDomain;
    }

    const steps = finishLaterFrom([
        termsStep(shared),
        entryCodeStep(shared),
        domainStep(shared),
        identityGenerationStep(shared),
        paymentStep(shared),
        completionStep(shared),
        domainVerificationStep(shared),
        deviceSetupStep(shared),
        emailVerificationStep(delegateClaimSteps(shared)),
        phoneVerificationStep(delegateClaimSteps(shared)),
        orgRecoveryKitOptInStep(shared),
        finalizeStep(shared),
    ], 'completion');
    const wizard = new Wizard(card, {
        steps,
        onComplete: () => callbacks.onComplete(),
        onCancel: () => {
            // Before the organization exists, leaving discards the stash.
            // From the step that creates the account on, the button says
            // "Finish later" and the stash stays — it is the only way back in, and
            // an account left unfinished is purged after 7 days (auth/wizard-exit.ts).
            if (!keepsRegistrationOnExit(steps, wizard.getCurrentIndex())) clearRegistrationState();
            callbacks.onCancel();
        },
        showCancel: true,
    });

    if (options.resumeFromPayment && (shared['registrationId'] || shared['orgCid'] || shared['invitationCode'])) {
        void (async () => {
            await ensureResumedOrgSession(shared);
            if (resumeFinalize || shared['kitDecided']) {
                wizard.goTo('finalize');
            } else {
                wizard.goTo('completion');
            }
        })();
    }
}

// ============================================================================
// Stash + resume helpers
// ============================================================================

function orgStashSync(shared: Record<string, unknown>, patch: {
    finalizeStarted?: boolean;
    resetCardSealedToken?: string;
    resetCardCreatedAt?: string;
    totpStored?: boolean;
} = {}): void {
    const prior = loadOnboardingStash();
    saveOnboardingStash({
        v: 1,
        savedAt: Date.now(),
        registrationType: 'organization',
        registrationId: (shared['registrationId'] as string | undefined) ?? prior?.registrationId,
        nonce: (shared['nonce'] as string | undefined) ?? prior?.nonce,
        voucherCode: (shared['voucherCode'] as string | undefined) ?? prior?.voucherCode,
        invitationCode: (shared['invitationCode'] as string | undefined) ?? prior?.invitationCode,
        personalSessionToken: prior?.personalSessionToken,
        finalizeStarted: patch.finalizeStarted ?? prior?.finalizeStarted,
        resetCardSealedToken: patch.resetCardSealedToken
            ?? (shared['resetCardSealedToken'] as string | undefined) ?? prior?.resetCardSealedToken,
        resetCardCreatedAt: patch.resetCardCreatedAt
            ?? (shared['resetCardCreatedAt'] as string | undefined) ?? prior?.resetCardCreatedAt,
        totpStored: patch.totpStored ?? prior?.totpStored,
        shared,
    });
}

// ── the founding delegate's claim steps ─────────────────────────────────────
// Round 6, §13.3: the shared implementation, pointed at the DELEGATE's own
// attr_encrypt pair. Completion already started the delegate session and loaded
// the delegate bag, so nothing here has to switch identities — but the keys
// must come from the delegate's bag, never the organization's, or the claims
// would be encrypted to an account that will never read them.

function delegateClaimSteps(shared: Record<string, unknown>): ClaimStepsConfig {
    return {
        shared,
        stashSync: () => orgStashSync(shared),
        attrPublicKey: () => (shared['delegatePublicKeys'] as Record<string, string>)['attr_encrypt'],
        attrPrivateKey: () =>
            getKeyBagEntry(shared['delegateKeyBag'] as KeyBag, 'attr_encrypt')!.privateKey,
        // An organization invitation may fix the FOUNDING DELEGATE's email and/or
        // phone (§14.3): an organization account has no channels of its own, so
        // those are the ones the buyer addressed the offer to, and the node binds
        // the invitation to this delegate's account at completion.
        emailFixed: () => shared['invitationEmailFixed'] === true,
        phoneFixed: () => shared['invitationPhoneFixed'] === true,
        valueSalt: () => shared['invitationValueSalt'] as string | undefined,
        copy: {
            emailIntro: 'Add an email address for YOUR delegate account — the account you sign in '
                + 'with to act for the organization — and verify it. Registration completes after '
                + 'both your email and phone are verified.',
            phoneIntro: 'Add a phone number that can receive SMS for your delegate account, and '
                + 'verify it with the code we text you.',
        },
    };
}

/** Revive a resumed org registration: the delegate session usually survives in
 *  localStorage; if not, log in with the THROWAWAY delegate password. */
async function ensureResumedOrgSession(shared: Record<string, unknown>): Promise<void> {
    const delegateCid = shared['delegateCid'] as string | undefined;
    if (!delegateCid) return;

    if (shared['delegateKeyBag']) keys.load(shared['delegateKeyBag'] as KeyBag);

    try {
        await accountApi.getAccountInfo();
        return;
    } catch { /* dead session — revive below */ }

    try {
        const authHash = await computeAuthHash(String(shared['delegatePassword'] ?? ''), delegateCid);
        const resp = await accountApi.login({ cid: delegateCid, authHash });
        // A throwaway-password revival can never meet the Authenticator Key
        // gate — finalize stores the key with the gate OFF, and the optional
        // "Login code" step runs after the print gate in the same page
        // lifetime. If it somehow answers `totp_required` there is no session
        // to revive with, so fall through to the same handling as a failure.
        if (isTotpRequired(resp)) throw new Error('This account now requires an authenticator code to sign in');
        session.start(resp.sessionToken, {
            cid: resp.cid, accountType: resp.accountType, status: resp.status, sessionId: resp.sessionId,
        });
        identity.setActive({
            cid: resp.cid, accountType: 'delegate',
            organizationCid: shared['orgCid'] as string | undefined, roles: ['owner'],
        });
        deviceStorage.setActiveCid(resp.cid);
    } catch {
        // Neither session nor throwaway login works — the finalize step will
        // surface API errors; the fallback is operator support.
    }
}

// ============================================================================
// Step 1: Terms
// ============================================================================

function termsStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'terms',
        label: 'Terms',
        render(container) {
            const step = createElement('div', { className: 'cid-wizard-step' });

            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Organization Identity Agreement' }));

            const text = createElement('div', { className: 'cid-wizard-terms' });
            text.textContent = 'By registering an Organization Identity on Catalog.ID, you agree to the Organization Membership Agreement. '
                + 'All cryptographic keys are generated entirely in your browser. The server never receives private keys. '
                + 'Two identities will be created: the Organization identity and your Founding Delegate identity. '
                + 'Their credentials are printed — once — at the end of registration; you are solely responsible for '
                + 'keeping that printed document set safe. If you lose a supersecret and have no device with imported keys, '
                + 'that identity is irrecoverable by design.';
            step.appendChild(text);
            // The agreement itself, to read before accepting it (2026-10-15).
            step.appendChild(createAgreementLink(ORGANIZATION_TERMS_ASSET));

            const checkRow = createElement('div', { className: 'cid-auth-checkbox-row' });
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.id = 'org-terms-accept';
            if (shared['termsAccepted']) checkbox.checked = true;
            const label = createElement('label', { textContent: 'I have read and accept the Organization Membership Agreement' });
            label.setAttribute('for', 'org-terms-accept');
            checkRow.appendChild(checkbox);
            checkRow.appendChild(label);
            step.appendChild(checkRow);

            checkbox.addEventListener('change', () => { shared['termsAccepted'] = checkbox.checked; });

            // Registration ends at a printer — there is no download and no
            // second chance, so ask BEFORE any money or keys move.
            const printerRow = createElement('div', { className: 'cid-auth-checkbox-row' });
            const printerCheckbox = document.createElement('input');
            printerCheckbox.type = 'checkbox';
            printerCheckbox.id = 'org-terms-printer';
            if (shared['printerAvailable']) printerCheckbox.checked = true;
            const printerLabel = createElement('label', {
                // The size, so the member can tell whether their printer is up to it.
                textContent: printerCheckboxLabel('organization'),
            });
            printerLabel.setAttribute('for', 'org-terms-printer');
            printerRow.appendChild(printerCheckbox);
            printerRow.appendChild(printerLabel);
            step.appendChild(printerRow);

            printerCheckbox.addEventListener('change', () => { shared['printerAvailable'] = printerCheckbox.checked; });

            container.appendChild(step);
        },
        validate() {
            if (shared['termsAccepted'] !== true) return 'You must accept the Organization Membership Agreement';
            return shared['printerAvailable'] === true
                || 'Confirm that you have a printer available — the document set is printed, never downloaded';
        },
    };
}

// ============================================================================
// Step 1b: Invitation or voucher code (registration-restructure §14.3)
// ============================================================================

/**
 * The wizard's own front door. A signed-in member arrives here from the gear
 * menu, never through the anonymous entry page — so this is where an
 * organization invitation or a voucher is taken, and it sits BEFORE the Domain
 * step because an invitation may fix the domain and the Identity step builds
 * the domain claim from it. The decision is `org-entry-code.ts`; a code that
 * arrived from the entry page is shown here and passes through untouched.
 */
function entryCodeStep(shared: Record<string, unknown>): WizardStep {
    let input: HTMLInputElement | null = null;
    return {
        id: 'code',
        label: 'Code',
        render(container) {
            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Invitation or Voucher' }));
            step.appendChild(createElement('p', {
                className: 'cid-wizard-text',
                textContent: 'If you were sent an organization invitation, or bought a registration code, enter it '
                    + 'here. Otherwise leave this empty: you pay at the payment step.',
            }));

            const field = createElement('div', { className: 'cid-auth-field' });
            input = createElement('input', { className: 'cid-auth-input' });
            input.type = 'text';
            input.autocomplete = 'off';
            input.spellcheck = false;
            input.placeholder = 'INV-XXXX-XXXX-XXXX-XXXX or RGV-…';
            const invitation = shared['invitationCode'] as string | undefined;
            const voucher = shared['checkedVoucherCode'] as string | undefined;
            input.value = invitation ? formatInvitationCode(invitation)
                : voucher ? formatVoucherCode(voucher) : '';
            field.appendChild(input);
            step.appendChild(field);

            step.appendChild(createElement('p', {
                className: 'cid-pane-note',
                textContent: 'An invitation may fix the organization\'s domain — the next step then shows it. '
                    + 'The code is checked with the server when you continue; nothing is spent until the end.',
            }));
            container.appendChild(step);
        },
        async validate() {
            const raw = input?.value ?? '';
            // A code the entry page delivered, left as it was: no second round trip.
            const entry = classifyEntryCode(raw.trim());
            if (entry?.kind === 'invitation' && entry.code === shared['invitationCode']) return true;
            if (entry?.kind === 'voucher' && entry.code === shared['checkedVoucherCode']) return true;
            const result = await resolveOrganizationEntryCode(raw);
            if (result.kind === 'refused') return result.message;
            applyOrganizationEntryCode(shared, result);
            return true;
        },
    };
}

// ============================================================================
// Step 2: Domain
// ============================================================================

function domainStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'domain',
        label: 'Domain',
        render(container) {
            const step = createElement('div', { className: 'cid-wizard-step' });

            // An organization invitation may FIX the domain (§14.3). It is the one
            // restriction the node holds in the clear, so it is prefilled and
            // read-only here: the buyer paid for this domain, and a founder who
            // typed another would be refused at begin with nothing gained.
            const fixedDomain = shared['invitationDomain'] as string | undefined;

            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Organization Domain' }));
            step.appendChild(createElement('p', {
                className: 'cid-wizard-text',
                textContent: fixedDomain
                    ? 'Your invitation is for this domain. It will be registered as the primary domain claim.'
                    : 'Enter the domain name for your organization. This will be registered as the primary domain claim.',
            }));

            const fieldWrapper = createElement('div', { className: 'cid-auth-field' });
            const input = document.createElement('input');
            input.type = 'text';
            input.className = 'cid-auth-input';
            input.placeholder = 'example.com';
            input.value = fixedDomain ?? (shared['domain'] as string) ?? '';
            if (fixedDomain) {
                input.readOnly = true;
                shared['domain'] = fixedDomain;
            } else {
                input.addEventListener('input', () => { shared['domain'] = input.value.trim(); });
            }
            fieldWrapper.appendChild(input);
            step.appendChild(fieldWrapper);

            container.appendChild(step);
        },
        validate() {
            const domain = shared['domain'] as string | undefined;
            if (!domain || domain.length === 0) return 'Enter a domain name';
            if (!domain.includes('.') || domain.length < 3) return 'Enter a valid domain name';
            return true;
        },
    };
}

// ============================================================================
// Step 3: Identity Generation (throwaway credentials ×2 — never shown)
// ============================================================================

function identityGenerationStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'identity',
        label: 'Identity',
        hideNext: true,
        render(container, ctx) {
            if (shared['orgKeyBag']) {
                container.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: 'Organization and delegate keys already generated.' }));
                setTimeout(() => ctx.wizard.next(), 0);
                return;
            }

            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Generating Organization Identity' }));

            const progress = createProgressBar({ indeterminate: true, label: 'Generating keys...' });
            step.appendChild(progress.element);

            const statusEl = createElement('p', { className: 'cid-wizard-status' });
            step.appendChild(statusEl);

            container.appendChild(step);

            void (async () => {
                try {
                    // ── Organization credentials (throwaway supersecret; the org
                    //    PASSWORD is minted at completion and never kept — org access
                    //    goes through delegates, docs/cid-governance.md M15) ──
                    statusEl.textContent = 'Generating organization working credentials...';
                    const orgSupersecret = generateSupersecret();
                    shared['orgSupersecret'] = orgSupersecret;

                    statusEl.textContent = 'Generating organization key pairs (X25519 + ML-KEM-1024, Ed25519 + ML-DSA-65)...';
                    const orgAttrKP = kemKeygen();
                    const orgMsgKP = kemKeygen();
                    const orgEfsKP = kemKeygen();
                    const orgSigKP = signingKeygen();
                    const orgChatEncKP = kemKeygen();
                    const orgChatSigKP = signingKeygen();
                    const orgMailEncKP = kemKeygen();
                    const orgMailSigKP = signingKeygen();

                    const orgKeyBag: KeyBag = {
                        entries: [
                            { keyId: 'attr_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(orgAttrKP.secretKey) },
                            { keyId: 'msg_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(orgMsgKP.secretKey) },
                            { keyId: 'efs_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(orgEfsKP.secretKey) },
                            { keyId: 'main_signing_key', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(orgSigKP.secretSeed) },
                            // The org's chat identity. Both ride the wrapped-org-key loop
                            // below, so the founding delegate is chat-reachable in its org
                            // capacity from day one (chat migration §3, decided 2026-08-25).
                            { keyId: 'chat_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(orgChatEncKP.secretKey) },
                            { keyId: 'chat_sign', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(orgChatSigKP.secretSeed) },
                            // The org's mail identity, same posture (mail migration §5.5):
                            // inbound mail to an org/role address is encrypted to
                            // `mail_encrypt`, and members reply under certs the org's
                            // `mail_sign` root issues. Org ROOT PUBLICATION stays deferred —
                            // minting the keys is custody, not publication.
                            { keyId: 'mail_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(orgMailEncKP.secretKey) },
                            { keyId: 'mail_sign', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(orgMailSigKP.secretSeed) },
                        ],
                    };
                    shared['orgKeyBag'] = orgKeyBag;
                    shared['orgAttrPublicKey'] = aes.toBase64(orgAttrKP.publicKey);
                    shared['orgPublicKeys'] = {
                        attr_encrypt: aes.toBase64(orgAttrKP.publicKey),
                        msg_encrypt: aes.toBase64(orgMsgKP.publicKey),
                        efs_encrypt: aes.toBase64(orgEfsKP.publicKey),
                        main_signing_key: aes.toBase64(orgSigKP.publicKey),
                        chat_encrypt: aes.toBase64(orgChatEncKP.publicKey),
                        chat_sign: aes.toBase64(orgChatSigKP.publicKey),
                        mail_encrypt: aes.toBase64(orgMailEncKP.publicKey),
                        mail_sign: aes.toBase64(orgMailSigKP.publicKey),
                    };

                    statusEl.textContent = 'Encrypting organization key bag (Argon2id)...';
                    progress.setLabel('Encrypting organization key bag...');
                    shared['orgEncryptedKeyBag'] = await encryptKeyBag(orgKeyBag, orgSupersecret);

                    // orgAuthHash needs the org CID — deferred to the completion step,
                    // after /register/begin assigns the CID.

                    // ── Delegate credentials (throwaway) ──────────────────
                    statusEl.textContent = 'Generating delegate working credentials...';
                    const delegatePassword = generatePassword();
                    shared['delegatePassword'] = delegatePassword;
                    const delegateSupersecret = generateSupersecret();
                    shared['delegateSupersecret'] = delegateSupersecret;

                    statusEl.textContent = 'Generating delegate key pairs (X25519 + ML-KEM-1024, Ed25519 + ML-DSA-65)...';
                    const delegateAttrKP = kemKeygen();
                    const delegateMsgKP = kemKeygen();
                    const delegateEfsKP = kemKeygen();
                    const delegateSigKP = signingKeygen();
                    const delegateChatEncKP = kemKeygen();
                    const delegateChatSigKP = signingKeygen();
                    const delegateMailEncKP = kemKeygen();
                    const delegateMailSigKP = signingKeygen();

                    const delegateKeyBag: KeyBag = {
                        entries: [
                            { keyId: 'attr_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(delegateAttrKP.secretKey) },
                            { keyId: 'msg_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(delegateMsgKP.secretKey) },
                            { keyId: 'efs_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(delegateEfsKP.secretKey) },
                            { keyId: 'main_signing_key', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(delegateSigKP.secretSeed) },
                            { keyId: 'chat_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(delegateChatEncKP.secretKey) },
                            { keyId: 'chat_sign', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(delegateChatSigKP.secretSeed) },
                            { keyId: 'mail_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(delegateMailEncKP.secretKey) },
                            { keyId: 'mail_sign', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(delegateMailSigKP.secretSeed) },
                        ],
                    };
                    shared['delegateKeyBag'] = delegateKeyBag;
                    shared['delegateAttrPublicKey'] = aes.toBase64(delegateAttrKP.publicKey);
                    shared['delegatePublicKeys'] = {
                        attr_encrypt: aes.toBase64(delegateAttrKP.publicKey),
                        msg_encrypt: aes.toBase64(delegateMsgKP.publicKey),
                        efs_encrypt: aes.toBase64(delegateEfsKP.publicKey),
                        main_signing_key: aes.toBase64(delegateSigKP.publicKey),
                        chat_encrypt: aes.toBase64(delegateChatEncKP.publicKey),
                        chat_sign: aes.toBase64(delegateChatSigKP.publicKey),
                        mail_encrypt: aes.toBase64(delegateMailEncKP.publicKey),
                        mail_sign: aes.toBase64(delegateMailSigKP.publicKey),
                    };

                    statusEl.textContent = 'Encrypting delegate key bag (Argon2id)...';
                    progress.setLabel('Encrypting delegate key bag...');
                    shared['delegateEncryptedKeyBag'] = await encryptKeyBag(delegateKeyBag, delegateSupersecret);

                    // delegateAuthHash needs the delegate CID — deferred to the completion step.

                    // ── Domain claim ──────────────────────────────────────
                    statusEl.textContent = 'Encrypting domain claim...';
                    progress.setLabel('Creating domain claim...');
                    const domainValue = shared['domain'] as string;
                    const domainEncrypted = await encryptClaim(domainValue, orgAttrKP.publicKey);
                    const domainClaimUuid = crypto.randomUUID();

                    let validatorEncryptedKey: string | undefined;
                    try {
                        const { publicKey: validatorKeyB64 } = await claimsApi.getValidatorKey();
                        validatorEncryptedKey = await reEncryptClaimKey(
                            domainEncrypted.encryptedKey, orgAttrKP.secretKey, aes.fromBase64(validatorKeyB64),
                        );
                    } catch (e) {
                        console.error('Failed to prepare validator key for domain claim:', e);
                        // Validator key not available — domain verification will be skipped on the server
                    }

                    shared['domainClaim'] = {
                        claimUuid: domainClaimUuid,
                        encryptedValue: domainEncrypted.encryptedValue,
                        nonce: domainEncrypted.nonce,
                        encryptedKey: domainEncrypted.encryptedKey,
                        validatorEncryptedKey,
                    };

                    // ── Wrapped org keys for founding delegate ────────────
                    statusEl.textContent = 'Wrapping organization keys for delegate...';
                    const wrappedOrgKeys: { keyId: string; keyIndex: number; wrappedKey: string }[] = [];
                    for (const entry of orgKeyBag.entries) {
                        const wrapped = await wrapPrivateKey(entry.privateKey, delegateAttrKP.publicKey);
                        wrappedOrgKeys.push({ keyId: entry.keyId, keyIndex: 0, wrappedKey: wrapped });
                    }
                    shared['wrappedOrgKeys'] = wrappedOrgKeys;

                    orgStashSync(shared);

                    progress.setIndeterminate(false);
                    progress.setValue(100);
                    progress.setVariant('success');
                    progress.setLabel('Identities ready');
                    statusEl.textContent = '';

                    setTimeout(() => ctx.wizard.next(), 600);
                } catch (err) {
                    progress.setIndeterminate(false);
                    progress.setVariant('error');
                    progress.setLabel('Failed');
                    statusEl.textContent = err instanceof Error ? err.message : 'Key generation failed';
                    statusEl.className = 'cid-pane-error cid-pane-error--visible';
                }
            })();
        },
    };
}

// ============================================================================
// Step 4: Payment (Stripe redirect via Worker)
// ============================================================================

/**
 * Hand a voucher code to the Completion step, which redeems it. A synthetic
 * registrationId keeps the stash's payment-leg shape intact (restore requires
 * both id and nonce) without pretending a Stripe payment happened. The nonce is
 * unused on this path — the voucher itself is the reservation anchor server-side.
 */
function proceedWithVoucher(shared: Record<string, unknown>, code: string, wizard: WizardAPI): void {
    const nonce = freshNonce();
    const registrationId = `voucher_${nonce.slice(0, 16)}`;
    shared['nonce'] = nonce;
    shared['registrationId'] = registrationId;
    shared['voucherCode'] = code;
    persistRegistrationState({
        registrationType: 'organization',
        registrationId,
        nonce,
        shared,
        personalSessionToken: api.getToken() ?? undefined,
    });
    wizard.goTo('completion');
}

/** Exported for its tests (`checked-voucher-payment.test.ts`). */
export function paymentStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'payment',
        label: 'Payment',
        hideNext: true,
        render(container, ctx) {
            // An INVITATION already paid for this registration (§14.3): one line
            // and a button. Showing a fee and a voucher field to somebody holding
            // a paid invitation would only invite them to pay twice.
            if (shared['invitationCode']) {
                const covered = createElement('div', { className: 'cid-wizard-step' });
                covered.appendChild(createElement('h3', {
                    className: 'cid-wizard-heading', textContent: 'Registration Fee',
                }));
                covered.appendChild(createElement('p', {
                    className: 'cid-wizard-text',
                    textContent: 'Your invitation covers the registration fee.',
                }));
                const continueBtn = createElement('button', {
                    className: 'cs-btn cs-btn--yes cid-auth-submit',
                    textContent: 'Continue',
                });
                continueBtn.addEventListener('click', () => ctx.wizard.goTo('completion'));
                covered.appendChild(continueBtn);
                container.appendChild(covered);
                return;
            }

            // A code the Code step (or the entry page before it) already read with
            // the node: name it and go on, rather than ask for it a second time
            // (checked-voucher-step.ts).
            const checked = shared['checkedVoucherCode'] as string | undefined;
            if (checked) {
                renderCheckedVoucher(container, {
                    code: checked,
                    onContinue: () => proceedWithVoucher(shared, checked, ctx.wizard),
                    onUseAnother: () => {
                        delete shared['checkedVoucherCode'];
                        // Back from Completion, it was already taken; the field
                        // the founder asked for starts empty.
                        if (shared['voucherCode'] === checked) delete shared['voucherCode'];
                        ctx.wizard.goTo('payment');
                    },
                });
                return;
            }

            // Stripe Checkout can't run in the dashboard iframe; open a popup and
            // poll payment-status in place. Standalone browser keeps the
            // full-page redirect + resume-on-return flow (unchanged).
            const dashboard = isDashboardContext();

            const step = createElement('div', { className: 'cid-wizard-step' });

            const heading = createElement('h3', { className: 'cid-wizard-heading', textContent: 'Registration Fee' });
            step.appendChild(heading);
            const feeText = createElement('p', {
                className: 'cid-wizard-text',
                textContent: dashboard
                    ? 'A one-time registration fee is required to create your organization. Payment opens in a separate Stripe window; this page continues automatically once payment confirms.'
                    : 'A one-time registration fee is required to create your organization. Payment is handled by Stripe; you will be redirected and brought back here once payment confirms.',
            });
            step.appendChild(feeText);

            // Bits for the TWO locks an organization voucher carries (§4.13):
            // the account lock funds the ORGANIZATION's wallet, the founder lock
            // the FOUNDING DELEGATE's. Chosen here exactly as in the storefront;
            // the bounds arrive with the config below, the rate from the Worker.
            const bitsField = createBitsField({
                label: "Bits on the organization's wallet",
                hint: "spending money for the organization's services, released into its wallet at the end of registration.",
            });
            step.appendChild(bitsField.row);
            const founderField = createBitsField({
                label: "Bits for the founder's own wallet",
                hint: 'for the person who signs in for the organization; their own wallet is printed in their kit.',
            });
            step.appendChild(founderField.row);

            const errorEl = createElement('div', { className: 'cid-pane-error' });
            step.appendChild(errorEl);

            const statusEl = createElement('p', { className: 'cid-wizard-status' });
            step.appendChild(statusEl);

            let idleLabel = isTestHookActive() ? 'Pay (test hook — skip Stripe)' : 'Continue to payment';
            const payBtn = createElement('button', {
                className: 'cs-btn cs-btn--yes cid-auth-submit',
                textContent: idleLabel,
            });

            // Runtime payment mode (Payment:Mode on the server): the button stays
            // disabled until the config resolves so a click can't race it. Fails
            // closed — an unreachable config endpoint keeps payment required.
            let paymentWaived = false;
            payBtn.setAttribute('disabled', '');
            void (async () => {
                const cfg = await fetchRegistrationConfig();
                const offer = paymentStepOffer(cfg);
                paymentWaived = offer.waivedContinue;
                bitsField.setBounds(cfg.bits);
                founderField.setBounds(cfg.bits);
                if (!offer.pay && !offer.waivedContinue) {
                    // The registration gate (docs/cid-governance.md M12): this node
                    // registers by invitation only, or could not say — a code is
                    // the one way on. No checkout, no bits to choose, no storefront.
                    heading.textContent = 'Registration code';
                    feeText.textContent = offer.notice ?? '';
                    payBtn.remove();
                    bitsField.remove();
                    founderField.remove();
                    showCodeOnly();
                    return;
                }
                if (paymentWaived) {
                    feeText.textContent = 'Registration on this server is currently free — no payment is required.';
                    idleLabel = 'Continue';
                    payBtn.textContent = idleLabel;
                    // Nothing is sold on a waived node — no storefront, no bits to
                    // choose (D9) — but a code still redeems: its locks hold the
                    // bits it was minted with.
                    showWaivedCode();
                    bitsField.remove();
                    founderField.remove();
                }
                payBtn.removeAttribute('disabled');
                // The pilot Worker's euro rate is gone with it (apps/bitcash/docs/plans/bitcash-v2.md
                // M12): the labels say "100 000 bits" and nothing else, which is still true.
            })();

            payBtn.addEventListener('click', async () => {
                // On a waived node a code in the field is redeemed, never skipped.
                if (paymentWaived && waivedContinueUsesCode(codeInput.value)) {
                    void redeemTypedCode();
                    return;
                }
                payBtn.setAttribute('disabled', '');
                payBtn.textContent = paymentWaived ? 'Continuing...' : 'Preparing payment...';
                errorEl.classList.remove('cid-pane-error--visible');
                statusEl.textContent = '';

                const nonce = freshNonce();
                const personalSessionToken = api.getToken() ?? undefined;

                // Payment waived on this node (staging posture): no Stripe, no
                // proof — the server accepts the registration on the nonce alone.
                if (paymentWaived) {
                    const registrationId = `waived_${nonce.slice(0, 16)}`;
                    shared['nonce'] = nonce;
                    shared['proofToken'] = '';
                    shared['paymentWaived'] = true;
                    shared['registrationId'] = registrationId;
                    persistRegistrationState({
                        registrationType: 'organization',
                        registrationId,
                        nonce,
                        shared,
                        personalSessionToken,
                    });
                    ctx.wizard.goTo('completion');
                    return;
                }

                const chosenBits = bitsField.read();
                if (chosenBits.problem !== null) { showError(new Error(chosenBits.problem)); return; }
                const chosenFounder = founderField.read();
                if (chosenFounder.problem !== null) { showError(new Error(`Founder's bits: ${chosenFounder.problem}`)); return; }
                const bits = chosenBits.bits;
                const founderBits = chosenFounder.bits;

                // ── mint the voucher BEFORE paying (§4.3) ────────────────
                // An ORGANIZATION voucher carries TWO locks under ONE keypair:
                // slot 0 for the organization's own wallet, slot 1 for the
                // founding delegate's (§4.1, §4.13). Both lock keys go into the
                // checkout, and the webhook creates both.
                let minted: MintedVoucher;
                try {
                    payBtn.textContent = 'Preparing your registration…';
                    const batch = await mintVoucherBatch({
                        registrationType: 'organization', quantity: 1, bits, founderBits,
                    });
                    minted = batch.vouchers[0];
                    shared['voucherCode'] = minted.code;
                } catch (err) {
                    showError(err);
                    return;
                }

                // Test hook (inert in prod): mint a local proof and continue without Stripe.
                if (isTestHookActive()) {
                    try {
                        const proofToken = await mintLocalTestProof(
                            'organization', nonce, 1, [voucherCodeHash(minted.lookupIdHex)]);
                        const registrationId = `test_${nonce.slice(0, 16)}`;
                        shared['nonce'] = nonce;
                        shared['proofToken'] = proofToken;
                        shared['registrationId'] = registrationId;
                        persistRegistrationState({
                            registrationType: 'organization',
                            registrationId,
                            nonce,
                            shared,
                            personalSessionToken,
                        });
                        if (dashboard) {
                            ctx.wizard.goTo('completion');
                        } else {
                            location.assign(`${window.location.origin}/auth/registration-complete?registrationId=${encodeURIComponent(registrationId)}`);
                        }
                    } catch (err) {
                        showError(err);
                    }
                    return;
                }

                if (dashboard) {
                    // Open the popup synchronously (within the click's user
                    // activation) before any await, or the browser may block it.
                    const popup = window.open('', 'cid-stripe-checkout', 'width=480,height=760');
                    await runDashboardCheckout(nonce, personalSessionToken, popup, minted, bits, founderBits);
                    return;
                }

                // Standalone browser: full-page redirect to Stripe; resume on return.
                try {
                    const created = await createPayment({
                        registrationType: 'organization',
                        nonce,
                        quantity: 1,
                        bits,
                        founderBits,
                        vouchers: [voucherSpecOf(minted)],
                        successUrl: getSuccessUrl(),
                        cancelUrl: getCancelUrl(),
                    });
                    persistRegistrationState({
                        registrationType: 'organization',
                        registrationId: created.registrationId,
                        nonce,
                        shared,
                        personalSessionToken,
                    });
                    location.assign(created.checkoutUrl);
                } catch (err) {
                    showError(err);
                }
            });
            step.appendChild(payBtn);

            // ── Voucher alternatives (registration-restructure §7.3) ─────────
            // Two of the three entry-dialog options live here, where the fee is
            // actually being settled: redeem a code you already hold, or go buy
            // codes for later / for someone else. The third — "pay and continue"
            // — is payBtn above, which now buys exactly one voucher and redeems
            // it on the spot (the conversion happens in the completion step, so
            // the Stripe popup, the Stripe redirect and the e2e test hook all
            // converge on one redemption path).
            const voucherSection = createElement('div', { className: 'cid-wizard-step' });

            const voucherIntro = createElement('p', {
                className: 'cid-wizard-text',
                textContent: 'Already have a voucher code? Enter it here instead of paying.',
            });
            voucherSection.appendChild(voucherIntro);

            const codeWrapper = createElement('div', { className: 'cid-auth-field' });
            const codeInput = createElement('input', { className: 'cid-auth-input' });
            codeInput.type = 'text';
            // Back from Completion, the code already taken.
            codeInput.value = (shared['voucherCode'] as string | undefined) ?? '';
            codeInput.placeholder = VOUCHER_CODE_PLACEHOLDER;
            codeInput.autocomplete = 'off';
            codeInput.spellcheck = false;
            codeWrapper.appendChild(codeInput);
            voucherSection.appendChild(codeWrapper);

            const redeemBtn = createElement('button', {
                className: 'cs-btn cs-btn--default cid-auth-submit',
                textContent: 'Use voucher',
            });
            redeemBtn.addEventListener('click', () => { void redeemTypedCode(); });
            codeInput.addEventListener('keydown', (e) => {
                if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); void redeemTypedCode(); }
            });
            voucherSection.appendChild(redeemBtn);

            const buyRow = createElement('div', { className: 'cid-auth-link-row' });
            const buyLink = createElement('a', {
                className: 'cid-auth-link',
                textContent: 'Buy voucher codes for later or for someone else',
            });
            buyLink.href = dashboardHref('/vouchers');
            // New tab: the wizard is mid-flight (keys generated, stash written), and
            // navigating away in place would force a resume for no reason. In the
            // dashboard the client is iframed, where an in-place navigation would
            // also take the whole pane with it.
            buyLink.target = '_blank';
            buyLink.rel = 'noopener';
            buyRow.appendChild(buyLink);
            voucherSection.appendChild(buyRow);

            step.appendChild(voucherSection);

            container.appendChild(step);

            // ── helpers (hoisted) ───────────────────────────────────────────

            /** A waived open node: the code section without the storefront. */
            function showWaivedCode(): void {
                voucherIntro.textContent = 'Have an organization registration code? Enter it to '
                    + 'receive the bits that come with it — or just Continue.';
                buyRow.remove();
            }

            /** The registration gate (M12): the code section alone — no storefront. */
            function showCodeOnly(): void {
                voucherIntro.textContent = 'Enter the organization registration code you were given.';
                buyRow.remove();
            }

            /** Validate a typed code client-side, then hand it to the completion
             *  step. The server re-validates everything; this only saves a round
             *  trip on an obvious typo. */
            async function redeemTypedCode(): Promise<void> {
                if (normaliseInvitationCode(codeInput.value)) {
                    errorEl.textContent = 'That is an invitation code. Go back to the Code step to use it — an '
                        + 'invitation may fix the domain, which comes before payment.';
                    errorEl.classList.add('cid-pane-error--visible');
                    return;
                }
                const normalized = normalizeVoucherCode(codeInput.value);
                if (!normalized) {
                    errorEl.textContent = 'That does not look like a voucher code. Codes start with RGV-.';
                    errorEl.classList.add('cid-pane-error--visible');
                    return;
                }
                errorEl.classList.remove('cid-pane-error--visible');
                redeemBtn.setAttribute('disabled', '');
                statusEl.textContent = 'Redeeming voucher…';
                proceedWithVoucher(shared, normalized, ctx.wizard);
            }

            function showError(err: unknown): void {
                payBtn.removeAttribute('disabled');
                payBtn.textContent = idleLabel;
                statusEl.textContent = '';
                errorEl.textContent = err instanceof Error ? err.message : 'Could not start payment';
                errorEl.classList.add('cid-pane-error--visible');
            }

            async function runDashboardCheckout(
                nonce: string, personalSessionToken: string | undefined,
                popup: Window | null, minted: MintedVoucher,
                bits: number, founderBits: number,
            ): Promise<void> {
                const controller = new AbortController();
                payBtn.textContent = 'Waiting for payment…';
                statusEl.textContent = 'Complete your payment in the Stripe window.';

                const cancelBtn = createElement('button', {
                    className: 'cs-btn cs-btn--default cid-auth-submit',
                    textContent: 'Cancel payment',
                });
                cancelBtn.addEventListener('click', () => {
                    controller.abort();
                    try { popup?.close(); } catch { /* ignore */ }
                });
                step.appendChild(cancelBtn);

                const result = await startPopupCheckoutAndAwaitProof({
                    popup,
                    registrationType: 'organization',
                    nonce,
                    quantity: 1,
                    bits,
                    founderBits,
                    vouchers: [voucherSpecOf(minted)],
                    abortSignal: controller.signal,
                    onStatus: (label) => { statusEl.textContent = label; },
                });

                cancelBtn.remove();

                if (result.status === 'complete') {
                    statusEl.textContent = 'Payment confirmed. Finalising your registration…';
                    shared['nonce'] = nonce;
                    shared['proofToken'] = result.proofToken;
                    shared['registrationId'] = result.registrationId;
                    persistRegistrationState({
                        registrationType: 'organization',
                        registrationId: result.registrationId,
                        nonce,
                        shared,
                        personalSessionToken,
                    });
                    ctx.wizard.goTo('completion');
                    return;
                }
                if (result.status === 'cancelled') {
                    payBtn.removeAttribute('disabled');
                    payBtn.textContent = idleLabel;
                    statusEl.textContent = 'Payment cancelled. You can try again.';
                    return;
                }
                showError(new Error(result.error));
            }
        },
    };
}

// ============================================================================
// Step 5: Completion (poll proof + call /register + session startup)
// ============================================================================

function completionStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'completion',
        label: 'Complete',
        hideNext: true,
        hideBack: true,
        render(container, ctx) {
            if (shared['orgCid']) {
                setTimeout(() => ctx.wizard.next(), 0);
                return;
            }

            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Confirming Your Organization Registration' }));

            const progress = createProgressBar({ indeterminate: true, label: 'Waiting for payment confirmation...' });
            step.appendChild(progress.element);

            const statusEl = createElement('p', { className: 'cid-wizard-status' });
            step.appendChild(statusEl);

            const errorEl = createElement('div', { className: 'cid-pane-error' });
            step.appendChild(errorEl);

            container.appendChild(step);

            void (async () => {
                try {
                    // ── invitation-backed registration (§14.3) ──────────────
                    // No proof, no voucher code and no registrationId: the
                    // invitation names the voucher it reserved. One scrypt over
                    // the code yields both the lookup id the node is addressed by
                    // and the fixed-value salt the founding delegate's claim steps
                    // hand to the validator.
                    const invitationCode = shared['invitationCode'] as string | undefined;
                    if (invitationCode) {
                        statusEl.textContent = 'Reading your invitation code...';
                        progress.setLabel('Reading your invitation...');
                        const secrets = await deriveInvitationSecrets(invitationCode);
                        shared['invitationValueSalt'] = secrets.saltHex;
                        // BEFORE /register: a redeemed invitation is no longer
                        // inspectable, and the claim steps need the flags.
                        const info = await inspectInvitation(secrets.lookupIdHex);
                        shared['invitationEmailFixed'] = info.emailFixed;
                        shared['invitationPhoneFixed'] = info.phoneFixed;
                        if (info.domain) shared['invitationDomain'] = info.domain;
                        orgStashSync(shared);
                        await finishOrgRegistration({
                            invitationLookupId: secrets.lookupIdHex,
                            domain: shared['domain'] as string,
                        });
                        return;
                    }

                    const registrationId = shared['registrationId'] as string | undefined;
                    const nonce = shared['nonce'] as string | undefined;
                    if (!registrationId || !nonce) {
                        throw new Error('Lost payment context. Please restart organization registration from the beginning.');
                    }

                    // Vouchers unify payment (registration-restructure §7): however the
                    // fee was settled, the proof becomes a voucher here and the
                    // registration redeems THAT. Conversion is idempotent on the Stripe
                    // session, so a crash between paying and stashing re-derives the
                    // same code rather than charging twice.
                    const voucherCode = shared['voucherCode'] as string | undefined;
                    let proofToken = shared['proofToken'] as string | undefined;

                    // The node is addressed by the LOOKUP ID, never the code
                    // (§4.1, D16): the code opens the Worker lock holding this
                    // voucher's bits, and a node able to see it could open that
                    // lock. Derived ONCE, up front: the conversion below needs
                    // it for its fallback and the reservation needs it after.
                    const voucherLookupId = voucherCode
                        ? (await deriveVoucherSecrets(voucherCode)).lookupIdHex : undefined;

                    // A PAID leg (Stripe popup, Stripe redirect, the e2e test
                    // hook) has a proof that must become a voucher ROW before
                    // the node can resolve that lookup id. The code was minted
                    // and stashed BEFORE the card was charged (§4.3), so its
                    // presence says nothing about whether the row exists yet —
                    // a typed voucher and a waived registration have no proof
                    // to convert. See `proof-conversion.ts` for the legs and the
                    // resume/retry rules.
                    if (needsProofConversion(shared, registrationId)) {
                        if (!voucherCode || !voucherLookupId) {
                            throw new Error('This browser has lost the registration code it bought. '
                                + 'It was shown nowhere else — contact support with your payment '
                                + 'reference.');
                        }
                        if (!proofToken) {
                            statusEl.textContent = `Polling payment status for ${registrationId}...`;
                            const result = await pollUntilComplete(registrationId, nonce, (r, attempt) => {
                                statusEl.textContent = `Status check ${attempt}: ${r.status}${r.status === 'error' ? ' — ' + r.error : ''}`;
                            });
                            if (result.status === 'error') throw new Error(result.error);
                            if (result.status !== 'complete') throw new Error('Payment is taking longer than expected. Refresh this page within an hour to retry.');
                            proofToken = result.proofToken;
                            shared['proofToken'] = proofToken;
                        }
                        const paidProof = proofToken;
                        statusEl.textContent = 'Issuing your registration voucher...';
                        progress.setLabel('Issuing voucher...');
                        // Conversion returns no codes (§4.3): this client
                        // committed only to the hash of the code in its stash.
                        await convertProofOnce({
                            purchase: () => purchaseVouchers({
                                paymentProofToken: paidProof,
                                nonce,
                                registrationType: 'organization',
                            }),
                            // A crash between a successful conversion and the
                            // flag write below, resumed after the proof's hour:
                            // the row is there and `inspect` resolves it.
                            rowExists: async () => { await inspectVoucher(voucherLookupId); return true; },
                        });
                        shared['voucherConverted'] = true;
                        // Stash before spending it: a retry or a resume must go
                        // straight to the reservation, never re-send the proof.
                        orgStashSync(shared);
                    }
                    await finishOrgRegistration(
                        voucherLookupId
                            ? { voucherLookupId }
                            : { paymentProofToken: proofToken ?? '', nonce });
                } catch (err) {
                    showFailure(err);
                }
            })();

            // ── helpers (hoisted) ───────────────────────────────────────────

            /**
             * The half of completion that is the same however the fee was
             * settled: reserve both CIDs, hash both throwaway passwords against
             * them, register, and start the founding delegate's session.
             * `authorization` is whichever of the three the node is shown.
             */
            async function finishOrgRegistration(authorization: {
                voucherLookupId?: string; paymentProofToken?: string; nonce?: string;
                invitationLookupId?: string; domain?: string;
            }): Promise<void> {
                statusEl.textContent = 'Reserving your organization and delegate identities...';
                progress.setLabel('Reserving CIDs...');
                const reservation = await organizationApi.beginRegisterOrganization(authorization);

                statusEl.textContent = 'Computing auth hashes (Argon2id × 2)...';
                // The organization's throwaway password lives for this call only:
                // never in `shared`, so never in the unencrypted stash, and retired
                // at finalize (docs/cid-governance.md M15).
                const orgAuthHash = await computeAuthHash(generatePassword(), reservation.orgCid);
                const delegateAuthHash = await computeAuthHash(shared['delegatePassword'] as string, reservation.delegateCid);

                statusEl.textContent = 'Registering organization with the server...';
                progress.setLabel('Registering...');

                const orgEnc = shared['orgEncryptedKeyBag'] as { keySalt: string; entries: { keyId: string; keyIndex: number; algorithm: string; encryptedPrivateKey: string; nonce: string }[] };
                const delegateEnc = shared['delegateEncryptedKeyBag'] as { keySalt: string; entries: { keyId: string; keyIndex: number; algorithm: string; encryptedPrivateKey: string; nonce: string }[] };
                const orgPublicKeys = shared['orgPublicKeys'] as Record<string, string>;
                const delegatePublicKeys = shared['delegatePublicKeys'] as Record<string, string>;
                const orgKeyBag = shared['orgKeyBag'] as KeyBag;
                const delegateKeyBag = shared['delegateKeyBag'] as KeyBag;
                const wrappedOrgKeys = shared['wrappedOrgKeys'] as { keyId: string; keyIndex: number; wrappedKey: string }[];
                const domainClaim = shared['domainClaim'] as { claimUuid: string; encryptedValue: string; nonce: string; encryptedKey: string; validatorEncryptedKey?: string };

                const resp = await organizationApi.registerOrganization({
                    orgCid: reservation.orgCid,
                    delegateCid: reservation.delegateCid,
                    orgAuthHash,
                    orgKeySalt: orgEnc.keySalt,
                    // Bag-only keys (chat_sign, …) carry no accountpublickey row —
                    // the server's keybag consistency check skip-lists them.
                    orgPublicKeys: orgKeyBag.entries
                        .filter(e => !isBagOnlyKeyId(e.keyId))
                        .map((e) => ({
                            keyId: e.keyId,
                            algorithm: e.algorithm,
                            publicKey: orgPublicKeys[e.keyId] ?? '',
                        })),
                    orgKeyBagEntries: orgEnc.entries,
                    delegateAuthHash,
                    delegateKeySalt: delegateEnc.keySalt,
                    delegatePublicKeys: delegateKeyBag.entries
                        .filter(e => !isBagOnlyKeyId(e.keyId))
                        .map((e) => ({
                            keyId: e.keyId,
                            algorithm: e.algorithm,
                            publicKey: delegatePublicKeys[e.keyId] ?? '',
                        })),
                    delegateKeyBagEntries: delegateEnc.entries,
                    wrappedOrgKeys,
                    domainClaim,
                    ...authorization,
                });

                shared['orgCid'] = resp.orgCid;
                shared['delegateCid'] = resp.delegateCid;
                shared['domainVerificationToken'] = resp.domainVerificationToken;

                // Registered under the founder's session, so at the founder's
                // origin (docs/cid-governance.md M7a): the organization and its
                // founding delegate are originated there, and the delegate's
                // sessions live there. Read before the delegate becomes active.
                const founderCid = deviceStorage.getActiveCid();
                session.start(resp.sessionToken,
                    { cid: resp.delegateCid, accountType: 'delegate', status: 'active', sessionId: resp.sessionId },
                    founderCid ? getStoredOrigin(founderCid) : null);
                identity.setActive({ cid: resp.delegateCid, accountType: 'delegate', organizationCid: resp.orgCid, roles: ['owner'] });
                keys.load(delegateKeyBag);
                deviceStorage.setActiveCid(resp.delegateCid);

                // The stash lives on until the print gate.
                orgStashSync(shared);

                progress.setIndeterminate(false);
                progress.setValue(100);
                progress.setVariant('success');
                progress.setLabel('Organization created');
                statusEl.textContent = '';

                step.appendChild(createElement('p', { className: 'cid-wizard-cid', textContent: `Organization CID: ${resp.orgCid}` }));
                step.appendChild(createElement('p', { className: 'cid-wizard-cid', textContent: `Delegate CID: ${resp.delegateCid}` }));

                setTimeout(() => ctx.wizard.next(), 1200);
            }

            function showFailure(err: unknown): void {
                progress.setIndeterminate(false);
                progress.setVariant('error');
                progress.setLabel('Registration failed');
                statusEl.textContent = '';
                errorEl.textContent = err instanceof Error ? err.message : 'Registration failed';
                errorEl.classList.add('cid-pane-error--visible');

                const retryBtn = createElement('button', {
                    className: 'cs-btn cs-btn--default cid-auth-submit',
                    textContent: 'Retry',
                });
                retryBtn.addEventListener('click', () => {
                    container.innerHTML = '';
                    completionStep(shared).render(container, ctx);
                });
                step.appendChild(retryBtn);
            }
        },
    };
}

// ============================================================================
// Step 6: Domain Verification
// ============================================================================

function domainVerificationStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'domain-verify',
        label: 'Verify Domain',
        nextLabel: 'Skip for now',
        hideBack: true,
        render(container, ctx) {
            const stepEl = createElement('div', { className: 'cid-wizard-step' });

            stepEl.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Verify Domain Ownership' }));

            const token = shared['domainVerificationToken'] as string | undefined;
            const domain = shared['domain'] as string;

            if (!token) {
                stepEl.appendChild(createElement('p', {
                    className: 'cid-wizard-text',
                    textContent: 'Domain validation could not be initiated. You can verify your domain later from the organization claims view.',
                }));
                container.appendChild(stepEl);
                return;
            }

            stepEl.appendChild(createElement('p', {
                className: 'cid-wizard-text',
                textContent: `Place the following token in a text file at https://${domain}/.well-known/catalogid.txt — the file must contain only this token, with no extra whitespace or HTML.`,
            }));

            const tokenRow = createElement('div', { className: 'cid-key-field' });
            const tokenCode = createElement('code', { className: 'cid-key-field-value' });
            tokenCode.textContent = token;
            tokenCode.style.wordBreak = 'break-all';
            tokenCode.style.userSelect = 'all';
            tokenRow.appendChild(tokenCode);
            tokenRow.appendChild(createCopyButton({ getText: () => token }));
            stepEl.appendChild(tokenRow);

            const statusEl = createElement('div', { className: 'cid-trust-success' });
            const errorEl = createElement('div', { className: 'cid-pane-error' });
            stepEl.appendChild(errorEl);
            stepEl.appendChild(statusEl);

            const verifyBtn = createElement('button', {
                className: 'cs-btn cs-btn--yes',
                textContent: 'Verify Domain',
            });
            verifyBtn.addEventListener('click', async () => {
                verifyBtn.setAttribute('disabled', '');
                errorEl.classList.remove('cid-pane-error--visible');
                statusEl.textContent = 'Verifying...';

                try {
                    const result = await claimsApi.verifyDomain(token, domain);
                    if (result.success) {
                        statusEl.textContent = 'Domain verified! Your organization is now active.';
                        shared['domainVerified'] = true;
                        orgStashSync(shared);
                        ctx.wizard.setNextLabel('Next');
                    } else {
                        statusEl.textContent = '';
                        errorEl.textContent = `Token not found at https://${domain}/.well-known/catalogid.txt — check that the file is publicly accessible.`;
                        errorEl.classList.add('cid-pane-error--visible');
                        verifyBtn.removeAttribute('disabled');
                    }
                } catch (e) {
                    statusEl.textContent = '';
                    errorEl.textContent = e instanceof Error ? e.message : 'Verification failed';
                    errorEl.classList.add('cid-pane-error--visible');
                    verifyBtn.removeAttribute('disabled');
                }
            });
            stepEl.appendChild(verifyBtn);

            stepEl.appendChild(createElement('p', {
                className: 'cid-pane-note',
                textContent: 'You can skip this step and verify later, but the organization will remain inactive until domain ownership is confirmed.',
            }));

            container.appendChild(stepEl);
        },
    };
}

// ============================================================================
// Step 7: Device Setup (delegate keys; before the print — the crash net)
// ============================================================================

function deviceSetupStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'device',
        label: 'Device',
        hideNext: true,
        hideBack: true,
        render(container, ctx) {
            if (shared['deviceSetupComplete']) {
                container.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: 'Device configured.' }));
                setTimeout(() => ctx.wizard.next(), 0);
                return;
            }

            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Device Setup' }));

            renderDeviceSetup(step, shared['delegateKeyBag'] as KeyBag, shared['delegateCid'] as string, {
                onSetup: async (
                    params: Parameters<import('../components/device-setup').DeviceSetupCallbacks['onSetup']>[0],
                    manifest: Parameters<import('../components/device-setup').DeviceSetupCallbacks['onSetup']>[1],
                ) => {
                    // `label` is plaintext — encrypt it here; never forward it as-is.
                    // The device belongs to the DELEGATE, so it is named under the
                    // delegate's attr_encrypt key, not the organization's.
                    const { label: plainLabel, ...rest } = params;
                    const delegateCid = shared['delegateCid'] as string;
                    const labelFields = await encryptLabelForDevice(delegateCid, plainLabel);
                    const resp = await deviceApi.setupDevice({ ...rest, ...labelFields });
                    deviceStorage.setDeviceId(delegateCid, resp.deviceId);
                    // Kept for the finalize step: the ceremony mints the
                    // delegate's `xrpl_master` AFTER this point and its device
                    // copy needs this same DEK (§11 trap, §13.1) — and the same
                    // key SELECTION, so a key the member just declined is not
                    // written here a minute later (device-setup-restructure
                    // §8, §7.3).
                    rememberDeviceKey(shared, delegateCid, params.deviceEncryptionKey, manifest);
                    // …and give the device-slot store the same key, so the
                    // default token the print step is about to mint can be
                    // cached under it (registration-wallets §4.12).
                    rememberSlotKey(delegateCid, aes.fromBase64(params.deviceEncryptionKey));
                    shared['deviceSetupComplete'] = true;
                    orgStashSync(shared);
                    ctx.wizard.next();
                },
                onSkip: () => {
                    deviceStorage.setNoLocalStorageMode(shared['delegateCid'] as string);
                    shared['deviceSetupComplete'] = true;
                    orgStashSync(shared);
                    ctx.wizard.next();
                },
            }, { confirmLabel: 'Configure device and continue' });

            container.appendChild(step);
        },
    };
}

// ============================================================================
// Step 8: Org Recovery Kit opt-in (G2 — 3-of-5 default; sheets split the FINAL
// org supersecret inside the finalize step)
// ============================================================================

/** Profile an organization kit falls back to when the stash names none (Addendum §8). */
const ORG_KIT_DEFAULTS = { thresholdK: 3, totalSharesN: 5 };

function orgRecoveryKitOptInStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'recovery-kit',
        label: 'Recovery',
        hideBack: true,
        nextLabel: 'Continue',
        render(container, ctx) {
            const step = createElement('div', { className: 'cid-wizard-step' });

            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Organization Recovery Sheets' }));
            step.appendChild(createElement('p', {
                className: 'cid-wizard-text',
                textContent: 'Recovery sheets split the ORGANIZATION supersecret across separate printed pages: '
                    + 'a quorum of them together recovers the organization if the supersecret page is ever '
                    + 'lost, while fewer reveal nothing. Pick the pattern that matches how the organization is '
                    + 'run. The sheets in the document set stay with it as the organization\'s safe copy; '
                    + 'custodians receive the additional copies printed after it, each the one its purpose '
                    + 'names. Any sheet can be printed again later from the organization\'s Recovery Kit pane.',
            }));

            const stored = readKitChoices(shared);

            // Not optional, and the pattern is the founder's own choice — none
            // is preselected — with at least one copy of every sheet to hand out
            // (staging test 2026-10-15).
            const fields = createRecoveryKitCardFields({
                kind: 'organization',
                idPrefix: 'org-kit',
                presetId: stored.presetId,
                labels: stored.labels.length > 0 ? stored.labels : undefined,
                extraCopies: stored.extraCopies.length > 0 ? stored.extraCopies : undefined,
                requirePresetChoice: true,
                minExtraCopies: REGISTRATION_MIN_EXTRA_COPIES,
                onPresetChange: () => ctx.wizard.setNextEnabled(true),
            });
            step.appendChild(fields.element);
            if (!fields.hasChoice()) ctx.wizard.setNextEnabled(false, 'Choose how the recovery sheets are distributed first');

            container.appendChild(step);

            this.validate = () => {
                if (!fields.hasChoice()) return 'Choose how the recovery sheets are distributed first';
                const picked = fields.read();
                writeKitChoices(shared, {
                    optIn: true,
                    presetId: picked.preset.id,
                    thresholdK: picked.preset.profile.thresholdK,
                    totalSharesN: picked.preset.profile.totalSharesN,
                    labels: picked.labels,
                    extraCopies: picked.extraCopies,
                });
                shared['kitDecided'] = true;
                orgStashSync(shared);
                return true;
            };
        },
        validate() { return true; }, // replaced in render()
    };
}

// ============================================================================
// Step 9: Finalize & Print
// ============================================================================

function finalizeStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'finalize',
        label: 'Print',
        hideNext: true,
        hideBack: true,
        render(container, ctx) {
            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Create & Print Your Credentials' }));

            const errorEl = createElement('div', { className: 'cid-pane-error' });
            const statusEl = createElement('p', { className: 'cid-wizard-status' });

            let ceremonyResult: EntropyCeremonyResult | null = null;
            /** Set when either release did not land — shown beside the kits. */
            let walletNotice: string | null = null;
            /** Set when the founder's wrap of the organization wallet did not land. */
            let custodyNotice: string | null = null;
            /** D27, from the node's config; null until read, or when the node names none (M12, D15). */
            let defaultTokenCaps: DefaultTokenTerms | null = null;
            void fetchRegistrationConfig().then(cfg => { defaultTokenCaps = cfg.defaultToken; });

            const ceremony = createEntropyCeremony({
                includeOrgSupersecret: true,
                passwordLabel: 'Delegate password',
                supersecretLabel: 'Delegate supersecret',
                xrplLabel: 'Delegate XRPL address',
                onReady: (result) => {
                    ceremonyResult = result;
                    finalizeBtn.style.display = result ? '' : 'none';
                },
                // TWO wallet keys from one stream (§4.13): the FOUNDING
                // DELEGATE's first, the ORGANIZATION's second. That order is
                // pinned by the org golden vector, and swapping it hands each
                // account the other's money.
                mintWalletKey: (next) => generateWalletKeyFrom(next, TYPE_CASH),
            });
            step.appendChild(ceremony);

            const finalizeBtn = createElement('button', {
                className: 'cs-btn cs-btn--yes cid-auth-submit',
                textContent: 'Finalize credentials',
            });
            finalizeBtn.style.display = 'none';
            step.appendChild(finalizeBtn);
            step.appendChild(statusEl);
            step.appendChild(errorEl);

            // The publish checkbox (D4, default OFF), for the founding delegate's
            // own receive address. The ORGANIZATION's cannot be published: a
            // publication belongs to the account that owns the claim, and an
            // organization account has no session (docs/cid-governance.md M15).
            const publishRow = createElement('div', { className: 'cid-auth-checkbox-row' });
            const publishBox = document.createElement('input');
            publishBox.type = 'checkbox';
            publishBox.id = 'cid-org-publish-fund-address';
            publishBox.checked = false;
            const publishLabel = createElement('label', {
                textContent: 'Publish my receive address so others can pay me',
            });
            publishLabel.setAttribute('for', publishBox.id);
            publishRow.appendChild(publishBox);
            publishRow.appendChild(publishLabel);
            step.appendChild(publishRow);
            const publishHint = createElement('p', {
                className: 'cid-pane-note',
                textContent: 'A receive address can only receive — it cannot spend. Publishing puts '
                    + 'yours on your public profile, and you can withdraw it later. The '
                    + 'organization\'s own receive address cannot be published yet.',
            });
            step.appendChild(publishHint);

            // Where to print is said beside the print button(s), once there is
            // something to print (components/print-button.ts PHYSICAL_PRINTER_NOTE).

            container.appendChild(step);

            finalizeBtn.addEventListener('click', async () => {
                if (!ceremonyResult?.orgSupersecret) return;
                const final = ceremonyResult;
                const finalOrgSupersecret = ceremonyResult.orgSupersecret;
                finalizeBtn.setAttribute('disabled', '');
                errorEl.classList.remove('cid-pane-error--visible');

                try {
                    const orgCid = shared['orgCid'] as string;
                    const orgKeyBag = shared['orgKeyBag'] as KeyBag;
                    const delegateKeyBag = shared['delegateKeyBag'] as KeyBag;

                    // 1. The founding delegate's Account Unlock Sheet (round 6,
                    //    §13.3) — sealed against the email and phone claims
                    //    verified two steps back. Issued BEFORE finalize so the
                    //    sealed token is on paper, and REUSED from the stash on
                    //    a reprint: re-issuing bumps the epoch and would kill a
                    //    sheet already printed (§11).
                    let sealedToken = shared['resetCardSealedToken'] as string | undefined;
                    if (!sealedToken) {
                        statusEl.textContent = 'Sealing your Account Unlock Sheet...';
                        const cardResp = await accountApi.issueResetCard({
                            emailClaimUuid: shared['emailClaimUuid'] as string,
                            emailEncryptedKey: shared['emailClaimEncKey'] as string,
                            phoneClaimUuid: shared['phoneClaimUuid'] as string,
                            phoneEncryptedKey: shared['phoneClaimEncKey'] as string,
                        });
                        sealedToken = cardResp.sealedToken;
                        shared['resetCardSealedToken'] = sealedToken;
                        shared['resetCardCreatedAt'] = cardResp.createdAt;
                        orgStashSync(shared, {
                            resetCardSealedToken: sealedToken, resetCardCreatedAt: cardResp.createdAt,
                        });
                    }

                    orgStashSync(shared, { finalizeStarted: true });

                    // 2. Delegate bag under the final delegate supersecret, through
                    //    the shared finalize sequence: the ceremony's `xrpl_master`
                    //    is appended (unless a resumed bag already carries one),
                    //    the bag is replaced account-scoped (challenge signed with
                    //    the delegate's own key, in memory), the new entry is copied
                    //    onto this device, and the delegate's final password is set
                    //    last. §13.1: the founding delegate gets the XRPL key; the
                    //    ORGANIZATION does not (open question, out of round 6).
                    //    Round 8 (§15.6): the founding delegate also gets the
                    //    ceremony's Authenticator Key. RESUME RULE — once the node
                    //    holds it, never PUT again; the sheet already printed would
                    //    be voided. The ORGANIZATION account gets none (§15.1).
                    const priorTotp = readStashedTotpSecret(shared);
                    const totpAlreadyStored = loadOnboardingStash()?.totpStored === true && priorTotp !== null;
                    const totpSecret = totpAlreadyStored ? priorTotp : final.totpSecret;
                    const finalizedDelegate = await finalizeCredentials({
                        cid: shared['delegateCid'] as string,
                        bag: delegateKeyBag,
                        supersecret: final.supersecret,
                        password: final.password,
                        xrplSeedEntropy: final.xrplSeedEntropy,
                        totpSecret,
                        totpAlreadyStored,
                        // The FOUNDER's wallet (§4.13) — drawn first of the two,
                        // and it lands in the DELEGATE's bag, not the org's.
                        bitcashWalletKey: final.founderBitcashWalletKey ?? null,
                        device: deviceCopyTarget(shared),
                        onStatus: (message) => { statusEl.textContent = message; },
                    });
                    shared['delegateKeyBag'] = finalizedDelegate.bag;
                    keys.load(finalizedDelegate.bag);
                    // The founder's own wallet, as the DOCUMENT now holds it —
                    // what the delegate kit prints (§13.1).
                    if (finalizedDelegate.bitcashWallet) {
                        shared['bitcashWalletKey'] = finalizedDelegate.bitcashWallet.key;
                    }
                    if (finalizedDelegate.totpStored && totpSecret) {
                        shared['totpSecret'] = base32Encode(totpSecret);
                    }
                    orgStashSync(shared, {
                        finalizeStarted: true, totpStored: finalizedDelegate.totpStored,
                    });
                    // "No local storage" mode holds no keys here by choice — only
                    // a device that DOES hold keys and missed the copy is a gap.
                    if (finalizedDelegate.deviceCopyMissing
                        && !deviceStorage.isNoLocalStorageMode(shared['delegateCid'] as string)) {
                        console.warn('[org-registration] the delegate XRPL master key was not copied to '
                            + 'this device — its first use here will ask for the supersecret');
                    }

                    // 3. ORG bag under the final org supersecret — same sequence,
                    //    org-scoped endpoint, the challenge signed with the ORG's
                    //    main_signing_key (held by this wizard). No auth hash: the
                    //    org password stays a never-disclosed throwaway.
                    const orgSigningEntry = getKeyBagEntry(orgKeyBag, 'main_signing_key');
                    if (!orgSigningEntry) throw new Error('Organization signing key unavailable');
                    // The ORGANIZATION's Recovery Kit set lives in the ORG bag
                    // (§16.6) — unlike the auth hash and the Authenticator Key,
                    // which orgFinalizeDeps refuses, an organization does have a
                    // recovery kit. Minted before the bag write, and discarded
                    // by finalizeCredentials on a resume whose bag already has one.
                    const kitChoices = readKitChoices(shared);
                    const mintedKitEntry = kitEntryFromChoices(kitChoices, ORG_KIT_DEFAULTS);
                    const finalizedOrg = await finalizeCredentials({
                        cid: orgCid,
                        bag: orgKeyBag,
                        supersecret: finalOrgSupersecret,
                        recoveryKitEntry: mintedKitEntry,
                        // The ORGANIZATION's wallet, drawn second (§4.13). It
                        // pays for organization work and is an entitled org key
                        // for owner and treasurer (D14), so it belongs in the
                        // ORG bag where the wraps can reach it.
                        bitcashWalletKey: final.orgBitcashWalletKey ?? null,
                        bitcashWalletLabel: 'Organization wallet',
                        onStatus: () => { statusEl.textContent = 'Re-encrypting organization keys...'; },
                        // The throwaway organization password goes (docs/cid-governance.md M15).
                        retirePassword: true,
                    }, orgFinalizeDeps(orgCid, orgSigningEntry.privateKey));
                    // The finalized org bag holds the organization wallet: the org
                    // kit prints its code from it, and the founder gets its wrap.
                    try {
                        await adoptFinalizedOrgBag({
                            shared, orgCid, founderCid: shared['delegateCid'] as string,
                            founderAttrPublicKey: aes.fromBase64((shared['delegatePublicKeys'] as Record<string, string>)['attr_encrypt']),
                            finalizedBag: finalizedOrg.bag,
                        }, { getWrappedOrgKeys: delegateApi.getWrappedOrgKeys, updateRoles: delegateApi.updateRoles, wrap: wrapPrivateKey });
                    } catch (err) {
                        console.warn('[org-registration] the organization wallet was not wrapped to the founding delegate:', err);
                        custodyNotice = 'Your access to the organization wallet could not be recorded on the server just now. '
                            + 'The wallet and its printed code are safe; an organization key rotation restores your access.';
                    }

                    // ── the money leg, TWICE (§4.5, §4.13) ──────────────────
                    // The organization voucher's ACCOUNT lock (slot 0) funds the
                    // ORGANIZATION's wallet; its FOUNDER lock (slot 1) funds the
                    // founding delegate's. Two releases, two addresses, two
                    // default tokens into two different cache slots. Neither can
                    // fail the print.
                    await runOrgMoneyLeg({
                        shared,
                        orgCid,
                        delegateCid: shared['delegateCid'] as string,
                        orgWallet: finalizedOrg.bitcashWallet?.key ?? null,
                        founderWallet: finalizedDelegate.bitcashWallet?.key ?? null,
                        caps: defaultTokenCaps ?? (await fetchRegistrationConfig()).defaultToken,
                        publishAddress: publishBox.checked,
                        onStatus: (message) => { statusEl.textContent = message; },
                        onNotice: (message) => { walletNotice = message; },
                        persist: () => orgStashSync(shared),
                    });
                    orgStashSync(shared);

                    // 4. Optional org recovery kit — splits the FINAL org supersecret.
                    //    The org-scoped /api/organization/{orgCid}/recoverykit endpoint
                    //    records it, so the org's own kit reminders work like a personal
                    //    account's.
                    let kit: RecoveryKit | null = null;
                    if (kitChoices.optIn && finalizedOrg.recoveryKitEntry) {
                        statusEl.textContent = 'Creating organization recovery sheets...';
                        const orgPublicKeys = shared['orgPublicKeys'] as Record<string, string>;
                        const entry = decodeRecoveryKitEntry(finalizedOrg.recoveryKitEntry.privateKey);
                        kit = await buildRecoveryKit({
                            entry,
                            cid: orgCid,
                            supersecret: finalOrgSupersecret,
                            cardExtraCopies: kitExtraCopiesFor(kitChoices, entry.totalSharesN),
                            // The bag AS WRITTEN — the kit entry rides in the
                            // bundle the sheets reference.
                            bag: finalizedOrg.bag,
                            // An ORGANIZATION has no account blobs
                            // (cid-encrypted-storage §5.1: org blobs wait for a
                            // consumer), and `/api/account/blobs` would answer
                            // for the FOUNDING DELEGATE's session anyway — so
                            // collecting here would file the delegate's
                            // documents in the organization's bundle.
                            blobs: [],
                            publicKeys: finalizedOrg.bag.entries.map(e => ({
                                keyId: e.keyId,
                                algorithm: e.algorithm,
                                publicKey: orgPublicKeys[e.keyId] ?? '',
                                keyIndex: e.keyIndex,
                                status: e.status,
                            })),
                        });
                        try {
                            await organizationApi.putOrgRecoveryKit(orgCid, {
                                recoverySetId: kit.recoverySetId,
                                thresholdK: kit.thresholdK,
                                totalSharesN: kit.totalSharesN,
                                bundleFingerprint: kit.bundle.bundle_fingerprint,
                            });
                        } catch {
                            // The kit itself is complete — the server record only
                            // powers reminders. Non-fatal.
                        }
                    }

                    // The delegate's final password was set inside step 1 (the
                    // shared sequence puts set-authhash last of its own writes);
                    // the ORG password stays a never-disclosed throwaway by design.

                    // 5. Two kits, two print jobs (round 5): print buttons and
                    //    the dual type-back gate render together — each print
                    //    must start synchronously from its own click (popup
                    //    activation rules), so nothing prints from THIS click.
                    // The founder's wallet key for the delegate kit: fetched
                    // NOW, so neither print click waits on the server first.
                    await refreshStashedWalletKey(shared);
                    statusEl.textContent = '';
                    // The money leg's verdict, when it was not "the bits
                    // arrived" (§4.5). Beside the kits, never instead of them.
                    for (const note of [walletNotice, custodyNotice]) {
                        if (note) step.appendChild(createElement('p', { className: 'cid-pane-note', textContent: note }));
                    }
                    // The publish choice was read by Finalize: from here on the box
                    // would only look like a choice still open.
                    publishRow.style.display = 'none';
                    publishHint.style.display = 'none';
                    renderOrgPrintAndGate(step, shared, final, kit, sealedToken, ctx);
                    finalizeBtn.style.display = 'none';
                    ceremony.style.display = 'none';
                } catch (err) {
                    finalizeBtn.removeAttribute('disabled');
                    statusEl.textContent = '';
                    errorEl.textContent = err instanceof Error ? err.message : 'Finalize failed';
                    errorEl.classList.add('cid-pane-error--visible');
                }
            });
        },
    };
}

/** Assemble the Organization Membership Kit (round 5: the organization's OWN
 *  document — cover, introduction, org credentials, org recovery sheets, with
 *  the Organization Identity Agreement appended; the founding delegate's
 *  credentials print in their own kit, see {@link buildDelegateKitPages}). */
function buildOrgPages(
    shared: Record<string, unknown>,
    final: EntropyCeremonyResult,
    kit: RecoveryKit | null,
): { pages: Content[][]; copySheets: CopySheet[]; print: BookletPrintOptions } {
    const orgCid = shared['orgCid'] as string;
    const domain = shared['domain'] as string | undefined;

    const orgHeader: AccountHeaderData = {
        cid: orgCid,
        accountTypeLabel: 'Organization',
        version: formatVersion(new Date()),
        orgDomain: domain,
    };

    // The ORGANIZATION's own wallet (§4.13): it pays for organization work and
    // its key is an entitled org key for owner and treasurer (D14). Read out of
    // the org bag for the same reason the personal kit reads its own.
    const orgWalletKey = walletKeyFromBag(shared['orgKeyBag'] as KeyBag | undefined);
    const orgReceiveAddress = typeof shared['orgBitsFundAddress'] === 'string'
        ? shared['orgBitsFundAddress'] as string : null;

    const title = bookletTitle('organization');
    const sections = [
        { title: 'Cover', pages: 1, inToc: false },
        { title: 'Introduction', pages: 1 },
        { title: 'Organization Credentials', pages: 1 },
    ];
    if (orgWalletKey) sections.push({ title: BITCASH_WALLET_PAGE_TITLE, pages: 1 });
    if (kit) sections.push(...recoverySheetSections(kit));
    const layout = buildKitLayout({
        sections,
        copySheets: kit ? recoveryCopyTitles(kit) : [],
        terms: { title: ORGANIZATION_TERMS_ASSET.title, pages: ORGANIZATION_TERMS_ASSET.pages },
    });

    const recoveryKit = kit ? { thresholdK: kit.thresholdK, totalSharesN: kit.totalSharesN } : null;
    const pages: Content[][] = [
        buildCoverPage({ title, header: orgHeader, recoveryKit }),
        buildFrontPage({ layout, recoveryKit }),
        buildCredentialsPage({
            header: orgHeader,
            supersecret: final.orgSupersecret!,
            organizationVariant: true,
            recoveryKit: recoveryKit ?? undefined,
        }),
    ];
    if (orgWalletKey) {
        pages.push(buildBitcashWalletPage({
            header: orgHeader, walletKey: orgWalletKey, receiveAddress: orgReceiveAddress,
            bitsPaidIn: shared['orgBitsReleased'] === true,
        }));
    }
    const copySheets: CopySheet[] = [];
    if (kit) {
        pages.push(...buildRecoveryCardPages(orgHeader, kit));
        copySheets.push(...buildRecoveryCardCopySheets(orgHeader, kit));
    }
    return {
        pages,
        copySheets,
        print: { title, totalPages: layout.totalPages, terms: ORGANIZATION_TERMS_ASSET, coverArtwork: true },
    };
}

/** Assemble the founding delegate's Catalog.ID Delegate Membership Kit — the
 *  shared builder (components/booklet/delegate-kit.ts), so the founding
 *  delegate and an invited one print the identical document: cover,
 *  introduction, delegate credentials with the Contact row, the Account Unlock
 *  Sheet (round 6, §13.3), the XRPL Master Key sheet (§13.1) and the Delegate
 *  Terms appended.
 *
 *  The XRPL key comes from the bag the delegate ACTUALLY has — the finalize
 *  sequence put the ceremony's entry there, and a resumed bag keeps the one it
 *  already had (§13.1). The sealed unlock token likewise comes from the stash
 *  on a reprint; neither is re-minted here. A missing either prints without
 *  that sheet rather than failing. */
/** The Authenticator Key the node already holds for the founding delegate, read
 *  back out of the stash's `shared` map (base32). Null when there is none. */
function readStashedTotpSecret(shared: Record<string, unknown>): Uint8Array | null {
    const value = shared['totpSecret'];
    if (typeof value !== 'string' || !value) return null;
    try {
        return base32Decode(value);
    } catch {
        return null;
    }
}

function buildDelegateKitPages(
    shared: Record<string, unknown>,
    final: EntropyCeremonyResult,
    sealedTokenB64: string | undefined,
): { pages: Content[][]; copySheets: CopySheet[]; print: BookletPrintOptions } {
    const delegateKeyBag = shared['delegateKeyBag'] as KeyBag;
    const xrplEntry = getKeyBagEntry(delegateKeyBag, XRPL_MASTER_KEY_ID);

    const { pages, copySheets, print } = buildDelegateKit({
        header: {
            cid: shared['delegateCid'] as string,
            accountTypeLabel: 'Delegate',
            version: formatVersion(new Date()),
            orgCid: shared['orgCid'] as string,
            orgDomain: shared['domain'] as string | undefined,
            orgRole: 'owner',
        },
        password: final.password,
        supersecret: final.supersecret,
        contact: {
            email: shared['emailAddress'] as string | undefined,
            phone: shared['phoneNumber'] as string | undefined,
        },
        unlockSheetToken: sealedTokenB64 ? aes.fromBase64(sealedTokenB64) : null,
        // The founding delegate's origin — where the founder's session registered
        // the organization (M7a).
        nodeHost: nodeHostFor(shared['delegateCid'] as string | undefined),
        xrplSeedEntropy: xrplEntry ? aes.fromBase64(xrplEntry.privateKey) : null,
        // Only what the node holds (§15.6) — a reprint prints the stash's
        // secret, never a freshly minted one nobody's login would check.
        totpSecret: readStashedTotpSecret(shared),
        // The FOUNDING DELEGATE's own wallet (§4.13) — a PERSONAL wallet, so it
        // comes out of the delegate's own `cid/bitcash-wallets` document
        // (cid-encrypted-storage §7.2) via the stash `refreshStashedWalletKey`
        // fills just before this runs, not out of the delegate bag. Its receive
        // address is Worker state and comes from the stash too.
        bitcashWalletKey: stashedWalletKey(shared),
        bitcashReceiveAddress: typeof shared['bitsFundAddress'] === 'string'
            ? shared['bitsFundAddress'] as string : null,
        bitcashBitsPaidIn: shared['bitsReleased'] === true,
        terms: DELEGATE_TERMS_ASSET,
    });
    return { pages, copySheets, print };
}

/** The two print buttons and the dual type-back gate (round 5). Each kit is
 *  its own print job, started synchronously from its own click; the gate then
 *  wants one line of the ORGANIZATION supersecret and one line of the DELEGATE
 *  supersecret, read off the two papers — proving both prints exist and are
 *  legible. Passing it marks the ORG and the founding delegate complete
 *  server-side (§8.5) and only THEN wipes the stash. */
function renderOrgPrintAndGate(
    step: HTMLElement,
    shared: Record<string, unknown>,
    final: EntropyCeremonyResult,
    kit: RecoveryKit | null,
    sealedTokenB64: string | undefined,
    ctx: WizardStepContext,
): void {
    const gate = createElement('div', { className: 'cid-wizard-step' });
    const orgLineNo = 1 + Math.floor(Math.random() * 4);
    const delegateLineNo = 1 + Math.floor(Math.random() * 4);
    const lineOf = (supersecret: string, lineNo: number): string =>
        supersecret.replace(/[-\s]/g, '').slice((lineNo - 1) * 12, lineNo * 12);
    const expectedOrg = lineOf(final.orgSupersecret!, orgLineNo);
    const expectedDelegate = lineOf(final.supersecret, delegateLineNo);

    // Each kit's exact size, now that both exist (the Terms step said "about").
    const pagesOf = (kit: { pages: Content[][]; copySheets: CopySheet[]; print: BookletPrintOptions }): number =>
        documentSetPageCount(kit.pages, kit.copySheets, kit.print);
    const orgPages = pagesOf(buildOrgPages(shared, final, kit));
    const delegatePages = pagesOf(buildDelegateKitPages(shared, final, sealedTokenB64));

    gate.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Print Both Kits' }));
    gate.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: `Two documents print separately: the Organization Membership Kit (${orgPages} pages, for `
            + `the organization's archive) and your own Delegate Membership Kit (${delegatePages} pages). `
            + 'Print both.',
    }));
    gate.appendChild(createElement('p', { className: 'cid-pane-note', textContent: PHYSICAL_PRINTER_NOTE }));

    const errorEl = createElement('div', { className: 'cid-pane-error' });

    let orgPrinted = false;
    let delegatePrinted = false;
    const updateGateAvailability = (): void => {
        if (orgPrinted && delegatePrinted) confirmBtn.removeAttribute('disabled');
    };

    // Each kit is assembled synchronously in its own click, so the print window
    // opens inside that click (components/print-button.ts); the wallet key the
    // delegate kit prints was fetched before these buttons were drawn.
    const printButton = (
        label: string,
        build: () => { pages: Content[][]; copySheets: CopySheet[]; print: BookletPrintOptions },
        markPrinted: () => void,
    ): HTMLElement => createPrintButton({
        label,
        errorEl,
        print: () => {
            const { pages, copySheets, print } = build();
            return printBooklet(pages, copySheets, print);
        },
        onPrinted: () => {
            markPrinted();
            updateGateAvailability();
        },
    });

    gate.appendChild(printButton('Print organization kit',
        () => buildOrgPages(shared, final, kit), () => { orgPrinted = true; }));
    gate.appendChild(printButton('Print delegate kit',
        () => buildDelegateKitPages(shared, final, sealedTokenB64), () => { delegatePrinted = true; }));

    gate.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Confirm Your Prints' }));
    gate.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: `Type LINE ${orgLineNo} of the ORGANIZATION supersecret (from the organization `
            + `kit's Credentials page) and LINE ${delegateLineNo} of the DELEGATE supersecret (from `
            + 'the delegate kit). This confirms both prints are complete and legible — after this '
            + 'step, the credentials exist nowhere but on your paper.',
    }));

    const lineInput = (placeholder: string): HTMLInputElement => {
        const wrapper = createElement('div', { className: 'cid-auth-field' });
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'cid-auth-input';
        input.placeholder = placeholder;
        input.autocomplete = 'off';
        input.spellcheck = false;
        wrapper.appendChild(input);
        gate.appendChild(wrapper);
        return input;
    };
    const orgInput = lineInput(`Organization supersecret line ${orgLineNo} (12 characters)`);
    const delegateInput = lineInput(`Delegate supersecret line ${delegateLineNo} (12 characters)`);

    gate.appendChild(errorEl);

    const confirmBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Confirm prints',
    });
    confirmBtn.setAttribute('disabled', ''); // enabled once both kits printed
    confirmBtn.addEventListener('click', () => {
        const check = (input: HTMLInputElement, expected: string, name: string): string | null => {
            const typed = input.value.replace(/[-\s]/g, '');
            if (typed === expected) return null;
            return typed.length !== 12
                ? `The ${name} line is not 12 characters — type the full line, dashes optional.`
                : `The ${name} line does not match the printed one — check for look-alike characters `
                    + 'and try again, or reprint.';
        };
        const problem = check(orgInput, expectedOrg, 'organization supersecret')
            ?? check(delegateInput, expectedDelegate, 'delegate supersecret');
        if (problem) {
            errorEl.textContent = problem;
            errorEl.classList.add('cid-pane-error--visible');
            return;
        }
        void confirmCompletion();
    });
    gate.appendChild(confirmBtn);

    // Shown only after a failed completion call: repeats the CALLS, not the
    // gate — the typed lines already matched, so re-typing proves nothing.
    const retryBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Retry',
    });
    retryBtn.style.display = 'none';
    retryBtn.addEventListener('click', () => { void confirmCompletion(); });
    gate.appendChild(retryBtn);

    step.appendChild(gate);

    /** Mark BOTH accounts complete, THEN wipe the stash (§8.5). The org and the
     *  founding delegate are purged independently, so the org goes first
     *  (owner-delegate session) and the delegate marks itself second; either
     *  failure leaves the stash intact so a resume lands back on this gate.
     *  Both endpoints are idempotent, so Retry re-runs the pair safely. */
    async function confirmCompletion(): Promise<void> {
        const orgCid = shared['orgCid'] as string;
        errorEl.classList.remove('cid-pane-error--visible');
        confirmBtn.setAttribute('disabled', '');
        retryBtn.setAttribute('disabled', '');
        retryBtn.style.display = 'none';
        orgInput.disabled = true;
        delegateInput.disabled = true;
        const priorLabel = confirmBtn.textContent;
        confirmBtn.textContent = 'Confirming with the server…';
        try {
            await completeOnboardingThenWipe(
                [
                    () => organizationApi.completeOrgOnboarding(orgCid),
                    () => accountApi.completeOnboarding(),
                ],
                clearRegistrationState,
            );
        } catch (err) {
            const message = err instanceof Error ? err.message : 'unknown error';
            errorEl.textContent = `Could not confirm completion with the server — ${message}. Retry.`;
            errorEl.classList.add('cid-pane-error--visible');
            confirmBtn.style.display = 'none';
            confirmBtn.textContent = priorLabel;
            retryBtn.removeAttribute('disabled');
            retryBtn.style.display = '';
            return;
        }
        // The optional Login code step (§15.6): after the gate has passed and
        // the account is marked complete, never before — it must not be able to
        // block the completion it follows. The founding delegate and the
        // invited delegate both have an Authenticator Key; the ORGANIZATION
        // account has none (§15.1).
        const totpSecret = readStashedTotpSecret(shared);
        if (totpSecret) {
            gate.innerHTML = '';
            // See device-setup-restructure §6.5 — the device is the founding
            // DELEGATE's, so the token is looked up under their cid.
            const loginCodeCid = shared['delegateCid'] as string;
            renderLoginCodeStep(gate, {
                cid: loginCodeCid,
                secret: totpSecret,
                onDone: renderDone,
                deviceToken: deviceStorage.getDeviceToken(loginCodeCid),
            });
            return;
        }
        renderDone();
    }

    function renderDone(): void {
        gate.innerHTML = '';
        gate.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Registration Complete' }));
        gate.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'Both kits are confirmed. The organization kit belongs in the organization\'s '
                + 'archive; the delegate kit is yours; distribute any recovery sheets to their '
                + 'custodians. You are now logged in as the founding delegate.',
        }));
        const finishBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes cid-auth-submit',
            textContent: 'Finish',
        });
        finishBtn.addEventListener('click', () => ctx.wizard.next());
        gate.appendChild(finishBtn);
    }
}


// ── the organization's money leg (registration-wallets §4.5, §4.13) ─────────

/** The ORGANIZATION's bitcash wallet key, out of the ORG bag. Read from the bag
 *  rather than the ceremony result so a resumed print and a reprint show the key
 *  the organization really has (the §13.1 rule the XRPL sheet already follows).
 *
 *  Organization-only: an org wallet is an entitled org key reached through the
 *  key wraps, because an organization has no derive root its delegates can use
 *  (cid-encrypted-storage §5.1, D14). The FOUNDING DELEGATE's own wallet is a
 *  personal wallet and lives in that delegate's `cid/bitcash-wallets` document
 *  — see {@link refreshStashedWalletKey}. */

/**
 * An organization voucher carries TWO locks under ONE keypair (§4.1, §4.13):
 *
 *   slot 0  the ACCOUNT lock   → the ORGANIZATION's wallet
 *   slot 1  the FOUNDER lock   → the FOUNDING DELEGATE's own wallet
 *
 * So the print step releases twice, mints two receive addresses and two default
 * tokens, and caches them in two different device slots — the delegate's under
 * `bitcash_default_token` (it is that delegate's own account), the
 * organization's under `bitcash_org_default_token:<orgCid>`, because an owner
 * may belong to several organizations and one slot would overwrite the next.
 *
 * The invitee path is the same two locks under the INVITATION's keypair: an
 * organization invitation relocks both (§4.4), so slot 0 and slot 1 keep their
 * meanings and only the key that opens them changes.
 *
 * Like every other money leg: NOTHING here can fail the print.
 */
async function runOrgMoneyLeg(args: {
    shared: Record<string, unknown>;
    orgCid: string;
    delegateCid: string;
    orgWallet: string | null;
    founderWallet: string | null;
    /** The default token's terms: caps and service types (minted unbound, §9.3). */
    caps: DefaultTokenTerms | null;
    publishAddress: boolean;
    onStatus: (message: string) => void;
    onNotice: (message: string) => void;
    /** Persist the stash: the codes `planWalletSetup` wrote into it must survive a reload before any ledger call. */
    persist: () => void;
}): Promise<void> {
    const { shared } = args;
    const deps = { worker: bitcashWorker() };

    // Whichever code this registration is riding on — the voucher the founder
    // typed or bought, or the invitation somebody personalized for them.
    let seed: Uint8Array | null = null;
    let publicKey: Uint8Array | null = null;
    const invitationCode = shared['invitationCode'] as string | undefined;
    const voucherCode = shared['voucherCode'] as string | undefined;
    if (invitationCode) {
        const secrets = await deriveInvitationSecrets(invitationCode);
        seed = secrets.seed;
        publicKey = secrets.publicKey;
    } else if (voucherCode) {
        const secrets: VoucherSecrets = await deriveVoucherSecrets(voucherCode);
        seed = secrets.seed;
        publicKey = secrets.publicKey;
    }

    const notices: string[] = [];

    // 1. The ORGANIZATION's wallet, from slot 0.
    if (args.orgWallet) {
        // Ledger, ref and codes written down before any ledger call (M12).
        const plan = await planWalletSetup({
            walletKey: args.orgWallet, stash: shared, stashKeys: { fund: 'orgBitsFundCode', token: 'orgBitsTokenCode' },
        });
        args.persist();
        const setup: WalletSetupResult = plan === null
            ? { release: null, receiveAddr: null, defaultToken: null, walletMissing: true }
            : await setUpWallet({
                walletKey: args.orgWallet,
                walletRef: plan.walletRef,
                networkId: plan.networkId,
                lock: seed && publicKey ? { lockKey: lockKeyFor(publicKey, 0), seed } : undefined,
                caps: args.caps,
                instruments: plan.instruments,
                alreadyReleased: readBitsStash(shared, 'org').bitsReleased,
                alreadyTokenIssued: readBitsStash(shared, 'org').defaultTokenIssued,
                onStatus: (m) => args.onStatus(`Organization wallet: ${m}`),
            }, deps);
        if (setup.release?.status === 'released') {
            shared['orgBitsReleased'] = true;
            shared['orgBitsWalletRef'] = setup.release.walletRef;
        }
        const notice = plan === null ? null : walletSetupNotice(setup);
        if (notice) notices.push(`Organization wallet: ${notice}`);
        if (setup.receiveAddr) shared['orgBitsFundAddress'] = setup.receiveAddr;
        if (setup.defaultToken) {
            shared['orgDefaultTokenIssued'] = true;
            // The ORG slot, keyed by orgCid — and cached under the DELEGATE's
            // identity, because that is the account whose device this is and
            // whose device key encrypts the slot.
            await setDeviceSlot(orgDefaultTokenSlot(args.orgCid), mintedDefaultTokenEntry({
                token: setup.defaultToken, walletRef: plan?.walletRef, serviceTypes: args.caps?.serviceTypes ?? [],
            }), args.delegateCid);
        }
    }

    // 2. The FOUNDING DELEGATE's wallet, from slot 1.
    if (args.founderWallet) {
        const plan = await planWalletSetup({
            walletKey: args.founderWallet, stash: shared, stashKeys: { fund: 'bitsFundCode', token: 'bitsTokenCode' },
        });
        args.persist();
        const setup: WalletSetupResult = plan === null
            ? { release: null, receiveAddr: null, defaultToken: null, walletMissing: true }
            : await setUpWallet({
                walletKey: args.founderWallet,
                walletRef: plan.walletRef,
                networkId: plan.networkId,
                lock: seed && publicKey ? { lockKey: lockKeyFor(publicKey, 1), seed } : undefined,
                caps: args.caps,
                instruments: plan.instruments,
                alreadyReleased: readBitsStash(shared).bitsReleased,
                alreadyTokenIssued: readBitsStash(shared).defaultTokenIssued,
                onStatus: (m) => args.onStatus(`Your own wallet: ${m}`),
            }, deps);
        if (setup.release?.status === 'released') {
            shared['bitsReleased'] = true;
            shared['bitsWalletRef'] = setup.release.walletRef;
        }
        const notice = plan === null ? null : walletSetupNotice(setup);
        if (notice) notices.push(`Your own wallet: ${notice}`);
        if (setup.receiveAddr) shared['bitsFundAddress'] = setup.receiveAddr;
        if (setup.defaultToken) {
            shared['defaultTokenIssued'] = true;
            await setDeviceSlot(DEFAULT_TOKEN_SLOT, mintedDefaultTokenEntry({
                token: setup.defaultToken, walletRef: plan?.walletRef, serviceTypes: args.caps?.serviceTypes ?? [],
            }), args.delegateCid);
        }
    }

    // 3. Publication (D4). The DELEGATE's address is published under the
    //    delegate's own attribute key — the session in hand. The ORGANIZATION's
    //    is not published: publication is scoped to the signed-in account
    //    (`PublicClaimController.Publish`), and an organization account has no
    //    session (docs/cid-governance.md M15), so no route can publish it.
    if (args.publishAddress && typeof shared['bitsFundAddress'] === 'string') {
        args.onStatus('Publishing your receive address…');
        const published = await publishReceiveAddress({
            receiveAddr: shared['bitsFundAddress'] as string,
            attrPublicKey: aes.fromBase64(
                (shared['delegatePublicKeys'] as Record<string, string>)['attr_encrypt']),
            // The DELEGATE's own attribute key: the address being published is
            // the founding delegate's, and it is their session in hand.
            attrPrivateKey: aes.fromBase64(getKeyBagEntry(
                shared['delegateKeyBag'] as KeyBag, 'attr_encrypt')!.privateKey),
        });
        shared['publishFundAddress'] = published.status === 'published';
        notices.push(publishOutcomeNotice(published));
    } else if (args.publishAddress) {
        notices.push(publishOutcomeNotice(null));
    }

    if (notices.length > 0) args.onNotice(notices.join(' '));
}

/**
 * Refresh the stashed FOUNDER wallet key from that delegate's own wallet
 * document before the delegate kit prints.
 *
 * §13.1's rule is "print what the account really has", and a personal wallet is
 * now a row in `cid/bitcash-wallets` (cid-encrypted-storage §7.2). The session
 * here belongs to the founding delegate, so `registrationWalletKey` answers for
 * the DELEGATE — never for the organization, whose wallet stays in the org bag
 * and is read by {@link walletKeyFromBag}.
 */
async function refreshStashedWalletKey(shared: Record<string, unknown>): Promise<void> {
    try {
        const held = await registrationWalletKey();
        if (held) shared['bitcashWalletKey'] = held;
    } catch {
        // Nothing about a print may throw here.
    }
}

/** The founder's wallet key for the delegate kit, or null. */
function stashedWalletKey(shared: Record<string, unknown>): string | null {
    const key = shared['bitcashWalletKey'];
    return typeof key === 'string' && key ? key : null;
}
